
import { supabase, isSupabaseConfigured } from './supabase';
import { 
  User, SchoolData, AcademicYear, Student, Mark, Payment, FeeStructure, FeePayment, 
  Plan, Guide, Announcement, Material, SupportTicket, SchoolStatus, Role, ClassGrade, 
  Subject, Assessment, AssessmentScore, GradingScheme, NurserySkill, NurseryEvaluation, 
  ExamRecord, Course, MarkRecord, ActiveSession, TeacherAssignment, AssessmentTypeConfig,
  Parent, SchoolLevel, TeacherCourseAssignment, UserNotification, SiteAvailability, Lesson
} from '../types';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

class DBService {
  private availabilityListeners: ((config: SiteAvailability) => void)[] = [];
  private cache = new Map<string, { value: any; expiry: number }>();

  private getCache<T>(key: string): T | null {
      const entry = this.cache.get(key);
      if (!entry) return null;
      if (entry.expiry < Date.now()) {
          this.cache.delete(key);
          return null;
      }
      return entry.value as T;
  }

  private setCache(key: string, value: any, ttlMs: number) {
      this.cache.set(key, { value, expiry: Date.now() + ttlMs });
  }

  private invalidateCache(keyPrefix?: string) {
      if (!keyPrefix) {
          this.cache.clear();
          return;
      }
      for (const key of this.cache.keys()) {
          if (key.startsWith(keyPrefix)) {
              this.cache.delete(key);
          }
      }
  }

  async fetchWithTimeout(url: string, options: any, timeout = 15000) {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      try {
          const response = await fetch(url, {
              ...options,
              signal: controller.signal
          });
          clearTimeout(id);
          return response;
      } catch (error: any) {
          clearTimeout(id);
          if (error.name === 'AbortError') {
              throw new Error('Request timed out. Please check your connection.');
          }
          throw error;
      }
  }

  private handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
    // We import auth_firebase from firebase.ts but it might not be initialized yet or we use the one from firebase/auth
    // For simplicity, we'll try to get the current user if possible
    const errInfo: FirestoreErrorInfo = {
      error: error instanceof Error ? error.message : String(error),
      authInfo: {
        userId: undefined, // We'll fill this if we can
        email: undefined,
        emailVerified: undefined,
        isAnonymous: undefined,
        tenantId: undefined,
        providerInfo: []
      },
      operationType,
      path
    };
    console.error('Firestore Error: ', JSON.stringify(errInfo));
    throw new Error(JSON.stringify(errInfo));
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      // Use getSession which doesn't trigger RLS policies
      // This is a reliable way to check if the client can talk to Supabase
      const { error } = await supabase.auth.getSession();
      
      if (error) {
        return { success: false, error: error.message };
      }
      
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Unknown connection error' };
    }
  }

  async init() {
    await this.testConnection();
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
        // console.log("No active session");
    }
  }

  // --- TEACHER ASSIGNMENTS ---
  async getTeacherAssignments(teacherId: string, schoolId?: string, academicYearId?: string): Promise<TeacherCourseAssignment[]> {
      try {
          let query = supabase
              .from('teacher_assignments')
              .select('*, classes(id, name), subjects(id, name)')
              .eq('teacher_id', teacherId);
          
          if (schoolId) {
              query = query.eq('school_id', schoolId);
          }

          if (academicYearId) {
              query = query.eq('academic_year_id', academicYearId);
          }

          const { data, error } = await query;
          
          let assignments: any[] = [];
          if (error) {
              if (error.code === 'PGRST204' || error.message.includes('academic_year_id')) {
                  console.warn("Retrying getTeacherAssignments without academic_year_id filter...");
                  let retryQuery = supabase
                    .from('teacher_assignments')
                    .select('*, classes(id, name), subjects(id, name)')
                    .eq('teacher_id', teacherId);
                  if (schoolId) retryQuery = retryQuery.eq('school_id', schoolId);
                  const { data: data2, error: error2 } = await retryQuery;
                  if (error2) throw error2;
                  assignments = (data2 as any[]) || [];
                  console.log(`[DB] getTeacherAssignments retry success. Count: ${assignments.length}`);
              } else {
                  console.error('Error fetching teacher assignments:', error);
                  return [];
              }
          } else {
              assignments = (data as any[]) || [];
          }

          // Try to fetch lesson info separately if possible
          let lessonMap = new Map();
          try {
              const { data: lessons } = await supabase.from('lessons').select('id, name');
              if (lessons) {
                  lessons.forEach(l => lessonMap.set(l.id, l.name));
              }
          } catch (e) {
              // Lessons table might be missing
          }
          
          return assignments.map(item => ({
              ...item,
              class_id: item.classes?.id || item.class_id,
              class_name: item.classes?.name || 'Unknown Class',
              subject_id: item.subjects?.id || item.subject_id,
              subject_name: item.subject_id === null ? (lessonMap.get(item.lesson_id) || item.subject_name || 'Class Teacher') : (item.subjects?.name || item.subject_name || 'Unknown Subject'),
              lesson_id: item.lesson_id,
              lesson_name: lessonMap.get(item.lesson_id) || (item.subject_id === null ? item.subject_name : null)
          })) as TeacherCourseAssignment[];
      } catch (e) {
          console.error("getTeacherAssignments exception:", e);
          return [];
      }
  }

  async getTeacherSubjects(userId: string, schoolId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('teacher_assignments')
      .select('class_id, subject_name')
      .eq('teacher_id', userId)
      .eq('school_id', schoolId);

    if (error) {
       console.error("Error fetching teacher subjects:", error);
       return [];
    }
    return data || [];
  }

  async getSchoolTeacherAssignments(schoolId: string): Promise<any[]> {
      try {
          const { data, error } = await supabase
              .from('teacher_assignments')
              .select('*, classes(id, name), subjects(id, name)')
              .eq('school_id', schoolId);
          if (error) throw error;
          return data || [];
      } catch (err) {
          console.error('getSchoolTeacherAssignments error:', err);
          return [];
      }
  }

  async removeTeacherAssignment(assignmentId: string | string[]) {
      const ids = Array.isArray(assignmentId) ? assignmentId : [assignmentId];
      if (ids.length === 0) return;

      const { error } = await supabase
          .from('teacher_assignments')
          .delete()
          .in('id', ids);
      if (error) {
          console.error("DELETE ASSIGNMENT ERROR:", error);
          throw error;
      }
  }

  async getClassLessonsBySchool(schoolId: string): Promise<Lesson[]> {
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('school_id', schoolId);

    if (error) {
        console.error("getClassLessonsBySchool error (ignoring if table missing):", error.message);
        return [];
    }
    return (data || []) as Lesson[];
  }

  async getClassLessons(classId: string, schoolId: string): Promise<Lesson[]> {
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('class_id', classId)
      .eq('school_id', schoolId);

    if (error) {
        console.warn("getClassLessons error (ignoring if table missing):", error.message);
        return [];
    }
    return (data || []) as Lesson[];
  }

  async assignTeacherLessons(selectedLessons: Lesson[], teacherId: string, classId: string, schoolId: string, academicYearId?: string) {
    if (!selectedLessons || selectedLessons.length === 0) return [];

    const payload = selectedLessons.map(l => ({
      id: crypto.randomUUID(),
      teacher_id: teacherId,
      class_id: classId,
      lesson_id: l.id,
      subject_name: l.name,
      school_id: schoolId,
      academic_year_id: academicYearId || 'ay_2024'
    }));

    console.log(`INSERTING ${payload.length} LESSON ASSIGNMENTS:`, payload);

    const { data, error } = await supabase
      .from('teacher_assignments')
      .insert(payload);

    if (error) {
      if (error.message.includes('does not exist') || error.message.includes('Could not find') || error.code === '42703' || error.code === 'PGRST205' || error.code === 'PGRST204') {
          console.warn("Retrying lesson assignment insert without new columns...");
          const partialPayload = payload.map(({ lesson_id, academic_year_id, ...rest }: any) => rest);
          const { data: data2, error: error2 } = await supabase
              .from('teacher_assignments')
              .insert(partialPayload);
          
          if (error2) {
              console.error("RETRY LESSON INSERT ERROR:", error2);
              throw error2;
          }
          return data2;
      }
      
      console.error("BULK LESSON INSERT ERROR:", error);
      throw error;
    }

    return data;
  }

  async assignTeacherCourses(assignments: any[]) {
    if (!assignments || assignments.length === 0) return [];

    const finalPayload = assignments.map(a => ({
      id: crypto.randomUUID(),
      teacher_id: a.teacher_id,
      class_id: a.class_id,
      subject_id: a.subject_id || null,
      lesson_id: (a as any).lesson_id || null,
      subject_name: a.subject_name,
      school_id: a.school_id,
      academic_year_id: a.academic_year_id || 'ay_2024'
    }));

    // First attempt: with lesson_id
    const { data, error } = await supabase
      .from('teacher_assignments')
      .insert(finalPayload);

    if (error) {
      if (error.message.includes('does not exist') || error.message.includes('Could not find') || error.code === '42703' || error.code === 'PGRST205' || error.code === 'PGRST204') {
          console.warn("Retrying assignment insert without new columns...");
          const partialPayload = finalPayload.map(({ lesson_id, academic_year_id, ...rest }: any) => rest);
          const { data: data2, error: error2 } = await supabase
              .from('teacher_assignments')
              .insert(partialPayload);
          
          if (error2) {
              console.error("RETRY COURSE INSERT ERROR:", error2);
              throw error2;
          }
          return data2;
      }
      
      console.error("BULK COURSE INSERT ERROR:", error);
      throw error;
    }

    return data;
  }

  async assignTeacherCourse(assignment: any) {
    return this.assignTeacherCourses([assignment]);
  }

  /**
   * Synchronizes teacher assignments between the user profile (JSONB) and the teacher_assignments table.
   */
  async syncTeacherAssignments(teacherId: string, schoolId: string, academicYearId?: string) {
      try {
          // 1. Get teacher profile
          const { data: teacher, error: userError } = await supabase
              .from('users')
              .select('*')
              .eq('id', teacherId)
              .single();
          
          if (userError || !teacher) throw new Error('Teacher not found');

          // 2. Get current table assignments
          const currentTableAssignments = await this.getTeacherAssignments(teacherId);
          
          // 3. Get all classes, subjects, lessons and academic years for resolution
          const [classes, subjects, lessons, years] = await Promise.all([
              this.getClasses(schoolId),
              this.getSubjects(schoolId),
              this.getClassLessonsBySchool(schoolId),
              this.getAcademicYears()
          ]);

          const currentYear = academicYearId || years.find(y => y.is_current && (y.school_id === 'all' || y.school_id === schoolId))?.id || 'ay_2024';

          const profileAssignments = teacher.assignments || [];
          
          // 4. Resolve profile assignments to full objects
          const resolvedAssignments = profileAssignments.map((pa: any) => {
              const cls = classes.find(c => (pa.class_id && c.id === pa.class_id) || c.name === pa.class_name);
              
              // Special case for Class Teacher which isn't in the subjects table
              if (pa.subject_name === 'Class Teacher') {
                  if (!cls) return null;
                  return {
                      teacher_id: teacherId,
                      school_id: schoolId,
                      class_id: cls.id,
                      class_name: cls.name,
                      subject_id: null,
                      subject_name: 'Class Teacher',
                      academic_year_id: currentYear,
                      academic_level: cls.academic_level || 'primary'
                  };
              }

              if (!cls) return null;
              const sub = subjects.find(s => s.name === pa.subject_name);
              const lesson = lessons.find(l => l.name === pa.subject_name && l.class_id === cls.id);
              
              if (!sub && !lesson) return null;
              
              return {
                  teacher_id: teacherId,
                  school_id: schoolId,
                  class_id: cls.id,
                  class_name: cls.name,
                  subject_id: sub?.id || null,
                  subject_name: sub?.name || lesson?.name || pa.subject_name,
                  lesson_id: lesson?.id || null,
                  academic_year_id: currentYear,
                  academic_level: cls.academic_level || 'primary'
              };
          }).filter(Boolean);

          // 5. Determine what to add and what to remove
          const toRemove = currentTableAssignments.filter(ta => 
              !resolvedAssignments.some((ra: any) => ra.class_id === ta.class_id && ra.subject_name === ta.subject_name)
          );

          const toAdd = resolvedAssignments.filter((ra: any) => 
              !currentTableAssignments.some(ta => ta.class_id === ra.class_id && ta.subject_name === ra.subject_name)
          );

          // 6. Execute updates
          if (toRemove.length > 0) {
              await this.removeTeacherAssignment(toRemove.map(ta => ta.id));
          }

          if (toAdd.length > 0) {
              await this.assignTeacherCourses(toAdd);
          }

          console.log(`Synced assignments for teacher ${teacherId}: ${toAdd.length} added, ${toRemove.length} removed.`);
      } catch (e: any) {
          console.error('syncTeacherAssignments error:', e);
          throw new Error('Failed to save teacher assignments: ' + (e.message || JSON.stringify(e)));
      }
  }

  // --- ACADEMICS & MARKS ---
  async getMarksApproval(schoolId: string, classId: string, subjectId: string, term: string, academicYearId: string): Promise<any> {
      const { data, error } = await supabase
          .from('marks_approvals')
          .select('*')
          .eq('school_id', schoolId)
          .eq('class_id', classId)
          .eq('subject_id', subjectId)
          .eq('term', term)
          .eq('academic_year_id', academicYearId)
          .maybeSingle();
      if (error) {
          console.error('Error fetching marks approval from Supabase:', error);
          return null;
      }
      return data;
  }

  async updateMarksApproval(approval: any) {
      // Use our backend API to bypass RLS for marks_approvals
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
      const response = await fetch(`${baseUrl}/api/admin/update-marks-approval`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(approval)
      });
      
      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update marks approval');
      }
  }

  async getClassProgress(schoolId: string, classId: string, term: string, academicYearId: string) {
      const classesData = await this.getClasses(schoolId);
      let actualClassId = classId;
      let actualClassName = classId;
      let classLevel = '';
      
      const classObj = classesData.find(c => c.name === classId || c.id === classId);
      if (classObj) {
          actualClassId = classObj.id;
          actualClassName = classObj.name;
          classLevel = classObj.academic_level || '';
      }
      
      const students = await this.getStudents(schoolId, actualClassId, undefined, actualClassName);
      const subjects = await this.getSubjects(schoolId);
      const examRecords = await this.getExamRecords(schoolId, actualClassId, undefined, term, academicYearId);
      const { data: nurseryEvaluationsData } = await supabase.from('nursery_evaluations').select('*');
      const nurseryEvaluations = nurseryEvaluationsData || [];
      const nurserySkills = await this.getNurserySkills(schoolId);
      
      const isNurseryLevel = classLevel.toLowerCase() === 'nursery' || 
                        actualClassName.toLowerCase().includes('nursery') || 
                        actualClassName.toLowerCase().startsWith('n');
                        
      // If we're classified as nursery but actually have exam_records, we should treat it as standard
      // or at least compute progress from what's actually entered.
      const hasNurseryEvals = nurseryEvaluations.some((e: any) => e.academic_year_id === academicYearId && e.term === term && students.some(s => s.id === e.student_id));
      const hasStandardEvals = examRecords.some(r => r.term === term && r.academic_year_id === academicYearId && students.some(s => s.id === r.student_id));
      
      const isNursery = isNurseryLevel && hasNurseryEvals && !hasStandardEvals;
      
      if (isNursery) {
          const studentIds = new Set(students.map(s => s.id));
          const classEvals = nurseryEvaluations.filter((e: any) => 
              studentIds.has(e.student_id) && 
              e.term === term && 
              e.academic_year_id === academicYearId
          );
          
          const totalPossible = students.length * nurserySkills.length;
          const enteredCount = classEvals.length;
          const percentage = totalPossible > 0 ? (enteredCount / totalPossible) * 100 : 0;
          
          const approval = await this.getMarksApproval(schoolId, actualClassId, 'nursery_all', term, academicYearId);
          
          return [{
              subject_id: 'nursery_all',
              subject_name: 'Nursery Evaluations',
              entered_count: enteredCount,
              total_count: totalPossible,
              percentage,
              status: approval?.status || 'draft'
          }];
      }

      const teachers = await this.getUsers(schoolId, 'teacher');
      const { data: assignmentsData } = await supabase.from('teacher_assignments').select('*').eq('school_id', schoolId);
      const assignments = assignmentsData || [];
      
      const assignedSubjectIds = new Set<string>();
      
      // 1. From ss_teacher_assignments (teacher_assignments)
      assignments.forEach((a: any) => {
          if ((a.class_id === actualClassId || a.class_name === actualClassName || a.class_name === classId) && a.school_id === schoolId) {
              if (a.subject_id) assignedSubjectIds.add(a.subject_id);
          }
      });
      
      // 2. From teacher profiles
      teachers.forEach(t => {
          if ((t.assigned_class === classId || t.assigned_class === actualClassName || t.assigned_class === actualClassId) && t.assigned_subject) {
              const subject = subjects.find(s => s.name === t.assigned_subject || s.id === t.assigned_subject);
              if (subject) assignedSubjectIds.add(subject.id);
          }
          if (t.assignments) {
              t.assignments.forEach(a => {
                  if (a.class_id === actualClassId || a.class_name === actualClassName || a.class_name === classId) {
                      const subject = subjects.find(s => s.name === a.subject_name || s.id === a.subject_id);
                      if (subject) assignedSubjectIds.add(subject.id);
                  }
              });
          }
      });
      
      // 3. From exam records
      examRecords.forEach(r => {
          if (r.term === term && r.academic_year_id === academicYearId && students.some(s => s.id === r.student_id)) {
              assignedSubjectIds.add(r.subject_id);
          }
      });
      
      const classSubjects = subjects.filter(s => assignedSubjectIds.has(s.id));
      
      const { data: approvalsData } = await supabase.from('marks_approvals').select('*').eq('school_id', schoolId);
      const approvals = approvalsData || [];

      const progress = classSubjects.map(subject => {
          const subjectRecords = examRecords.filter(r => 
              r.subject_id === subject.id && 
              r.term === term && 
              r.academic_year_id === academicYearId &&
              students.some(s => s.id === r.student_id)
          );
          
          // Support standard (score) entries as well as split-mark (test_score / exam_score) entries
          const enteredCount = subjectRecords.filter(r => 
              (r.score !== undefined && r.score !== null && r.score !== '') ||
              (r.test_score !== undefined && r.test_score !== null && r.test_score !== '') ||
              (r.exam_score !== undefined && r.exam_score !== null && r.exam_score !== '')
          ).length;
          const totalCount = students.length;
          const percentage = totalCount > 0 ? (enteredCount / totalCount) * 100 : 0;
          
          const approval = approvals.find((a: any) => 
              a.school_id === schoolId && 
              a.class_id === actualClassId && 
              a.subject_id === subject.id && 
              a.term === term && 
              a.academic_year_id === academicYearId
          );
          
          let status = 'draft';
          if (approval) {
              status = approval.status;
          } else if (subjectRecords.length > 0) {
              status = subjectRecords[0].status;
          }
          
          return {
              subject_id: subject.id,
              subject_name: subject.name,
              entered_count: enteredCount,
              total_count: totalCount,
              percentage,
              status
          };
      });
      
      return progress;
  }

  async getTeacherCourses(userId: string, academicYearId?: string): Promise<Course[]> {
      const assignments = await this.getTeacherAssignments(userId, undefined, academicYearId);
      
      // If no ID-based assignments found, try name-based fallback
      if (assignments.length === 0) {
          const { data: user } = await supabase.from('users').select('full_name').eq('id', userId).maybeSingle();
          if (user?.full_name) {
              const { data: nameAssignments } = await supabase
                .from('teacher_assignments')
                .select('*, classes(id, name), subjects(id, name)')
                .ilike('teacher_name', `%${user.full_name}%`);
                
              if (nameAssignments && nameAssignments.length > 0) {
                  // Merge these into the assignments logic
                  console.log(`[DB] Found ${nameAssignments.length} name-based assignments for ${user.full_name}`);
                  assignments.push(...nameAssignments);
              }
          }
      }

      // Attempt to retrieve profile assignments to get names if RLS blocked joins
      let profileAssignments: any[] = [];
      try {
          const { data: user } = await supabase.from('users').select('assignments').eq('id', userId).single();
          if (user && user.assignments) {
              profileAssignments = user.assignments;
          }
      } catch (e) {
          // ignore
      }
      
      const courses: Course[] = assignments.map(a => {
          let className = a.class_name;
          let subjectName = a.subject_name;
          
          if (className === 'Unknown Class' || className === '') {
              // Try to find the class name from profile assignments by matching subject name or id
              const match = profileAssignments.find(pa => pa.subject_name === subjectName || pa.subject_id === a.subject_id);
              if (match && match.class_name) {
                  className = match.class_name;
              } else {
                  // Fallback: if we still don't know the class name, we can hopefully use class_id as a fallback
                  className = a.class_id || className;
              }
          }
          
          return {
              id: `${a.class_id}_${subjectName}`.replace(/\s+/g, '_'),
              name: subjectName,
              class_id: a.class_id,
              subject_id: a.subject_id || '',
              class_group: className,
              teacher_id: userId,
              school_id: a.school_id,
              academic_year_id: academicYearId || 'ay_2024',
              max_marks: 100,
              status: 'draft'
          };
      });

      // Deduplicate by ID
      const uniqueCourses = courses.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
      return uniqueCourses;
  }

  private currentUserPromise: Promise<User | null> | null = null;

  async getCurrentUser(): Promise<User | null> {
      const cached = this.getCache<User>('current_user');
      if (cached) return cached;

      if (this.currentUserPromise) {
          return this.currentUserPromise;
      }

      this.currentUserPromise = (async () => {
          try {
              const { data: { session }, error: sessionError } = await supabase.auth.getSession();
              if (sessionError && (sessionError.message?.includes('fetch') || sessionError.message?.includes('Network') || (sessionError as any).status === 0)) {
                  throw sessionError;
              }
              if (!session) {
                  // Check if we are offline with a persistent profile cached locally
                  if (typeof window !== 'undefined') {
                      const fallbackUserStr = localStorage.getItem('ss_fallback_current_user');
                      if (fallbackUserStr) {
                          try {
                              const fallbackUser = JSON.parse(fallbackUserStr);
                              this.setCache('current_user', fallbackUser, 300000);
                              return fallbackUser;
                          } catch (_) {}
                      }
                  }
                  return null;
              }

              let { data: userData, error: userError } = await supabase
                  .from('users')
                  .select('*')
                  .eq('id', session.user.id)
                  .single();

              if (userError || !userData) {
                  const isFetchError = userError && (userError.message?.includes('fetch') || userError.message?.includes('Network') || (userError as any).status === 0);
                  if (isFetchError) {
                      throw new Error(userError?.message || 'Network fetch error');
                  }

                  console.warn('User profile not found in Supabase during session check. Attempting self-healing for ID:', session.user.id);
                  const newProfile = {
                      id: session.user.id,
                      email: session.user.email || '',
                      full_name: session.user.user_metadata?.full_name || session.user.email || 'User',
                      role: session.user.user_metadata?.role || 'teacher',
                      school_id: session.user.user_metadata?.school_id || null,
                      created_at: new Date().toISOString()
                  };
                  const { data: healedData, error: healError } = await supabase
                      .from('users')
                      .insert(newProfile)
                      .select('*')
                      .single();

                  if (healError) {
                      console.error('Self-healing in getCurrentUser failed:', healError);
                      const isHealFetchError = healError.message?.includes('fetch') || healError.message?.includes('Network') || (healError as any).status === 0;
                      if (isHealFetchError) {
                          throw new Error(healError.message || 'Network fetch error during self-healing');
                      }
                      return null;
                  }
                  userData = healedData;
              }

              // Reject if the user's status is inactive
              if (userData.status === 'inactive') {
                  console.warn('User status is inactive. Logging out.');
                  await this.logout();
                  return null;
              }

              // Reject if the school status is inactive or deleted
              if (userData.role !== 'platform_admin' && userData.school_id) {
                  const { data: schoolData } = await supabase
                      .from('schools')
                      .select('status')
                      .eq('id', userData.school_id)
                      .maybeSingle();

                  if (!schoolData || schoolData.status === 'inactive' || schoolData.status === 'suspended') {
                      console.warn('User school is inactive or suspended. Logging out.');
                      await this.logout();
                      return null;
                  }
              }

              if (typeof window !== 'undefined') {
                  let clientSessId = sessionStorage.getItem('ss_session_id');
                  if (!clientSessId && userData.session_id && userData.session_id !== 'terminated') {
                      sessionStorage.setItem('ss_session_id', userData.session_id);
                  } else if (!clientSessId) {
                      const newSessId = 'sess_' + Math.random().toString(36).substring(2) + '_' + Date.now();
                      sessionStorage.setItem('ss_session_id', newSessId);
                      const nowIso = new Date().toISOString();
                      // Wrap in try-catch to avoid blocking if network drops during session_id sync
                      try {
                          await this.updateUser(userData.id, { session_id: newSessId, last_active: nowIso });
                          userData.session_id = newSessId;
                          userData.last_active = nowIso;
                      } catch (_) {}
                  }
              }

              const prefs = userData.preferences || {};
              const userObj = {
                  ...userData,
                  avatar: userData.avatar || prefs.avatar || '',
                  phone: userData.phone || prefs.phone || '',
                  last_login: userData.last_login || ''
              } as User;

              if (typeof window !== 'undefined') {
                  try {
                      localStorage.setItem('ss_fallback_current_user', JSON.stringify(userObj));
                  } catch (_) {}
              }

              this.setCache('current_user', userObj, 3000);
              return userObj;
          } catch (e: any) {
              console.warn('getCurrentUser fetch/network issue encountered. Checking local fallback:', e);
              if (typeof window !== 'undefined') {
                  const fallbackUserStr = localStorage.getItem('ss_fallback_current_user');
                  if (fallbackUserStr) {
                      try {
                          const fallbackUser = JSON.parse(fallbackUserStr);
                          this.setCache('current_user', fallbackUser, 300000);
                          return fallbackUser;
                      } catch (_) {}
                  }
              }
              return null;
          } finally {
              this.currentUserPromise = null;
          }
      })();

      return this.currentUserPromise;
  }

  // --- MOCK PASSWORD HASHING ---
  // In a real application, this would be done on the server using bcrypt or argon2.
  // For this client-side mock, we use a simple base64 encoding to demonstrate the concept.
  private async hashPassword(password: string): Promise<string> {
      // Simulate async hashing delay
      await new Promise(resolve => setTimeout(resolve, 50));
      // Use encodeURIComponent to handle non-Latin1 characters safely
      return btoa(encodeURIComponent(`hashed_${password}_salt`));
  }

  private async comparePassword(password: string, hash: string): Promise<boolean> {
      const expectedHash = await this.hashPassword(password);
      if (expectedHash === hash) return true;
      
      // Fallback for plaintext passwords from before hashing was implemented
      if (password === hash) return true;
      
      return false;
  }

  async login(email: string, pass: string): Promise<{user: User | null, error: string | null}> {
      const e = email.toLowerCase().trim();
      const p = pass.trim();

      try {
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
              email: e,
              password: p
          });

          if (authError) {
              return { user: null, error: authError.message };
          }

          if (authData.user) {
              // Fetch user profile from our 'users' table
              const { data: userData, error: userError } = await supabase
                  .from('users')
                  .select('*')
                  .eq('id', authData.user.id)
                  .single();

              if (userError || !userData) {
                  console.warn('User profile missing in "users" table. Attempting self-healing for ID:', authData.user.id);
                  
                  // Self-healing: Create the profile if it's missing
                  const newProfile = {
                      id: authData.user.id,
                      email: authData.user.email,
                      full_name: authData.user.user_metadata?.full_name || authData.user.email,
                      role: authData.user.user_metadata?.role || 'teacher',
                      created_at: new Date().toISOString()
                  };

                  const { data: healedData, error: healError } = await supabase
                      .from('users')
                      .insert(newProfile)
                      .select('*')
                      .single();

                  if (healError) {
                      console.error('Self-healing failed:', healError);
                      return { user: null, error: 'User profile not found and could not be created. Please contact support.' };
                  }
                  
                  return { user: healedData as User, error: null };
              }

              const userToLogin: User = userData;

              // Check user status
              if (userToLogin.status === 'inactive') {
                  return { user: null, error: 'Your user account is inactive. Please contact your administrator.' };
              }

              // Check school status for non-platform admins
              if (userToLogin.role !== 'platform_admin' && userToLogin.school_id) {
                  const { data: schoolData, error: schoolError } = await supabase
                      .from('schools')
                      .select('*')
                      .eq('id', userToLogin.school_id)
                      .single();

                  if (schoolData) {
                      const isApproved = schoolData.status === 'active';
                      const getDaysRemaining = () => {
                          if(!schoolData.subscription_end_date) return 0;
                          const end = new Date(schoolData.subscription_end_date);
                          const now = new Date();
                          const diff = end.getTime() - now.getTime();
                          return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
                      };
                      const daysLeft = getDaysRemaining();
                      
                      if (!isApproved) {
                          let errorMsg = 'Your school account is not yet approved or is inactive.';
                          if (schoolData.status === 'pending') {
                              errorMsg = 'Your school registration is pending approval. Please check back later.';
                          } else if (schoolData.status === 'under_review' || schoolData.status === 'pending_payment') {
                              errorMsg = 'Your school registration is pending approval.';
                          } else if (schoolData.status === 'suspended') {
                              errorMsg = 'Your school account has been suspended.';
                          } else if (schoolData.status === 'rejected') {
                              errorMsg = `Your school registration was rejected. Reason: ${schoolData.rejection_reason || 'No reason provided.'}`;
                          }
                          return { user: null, error: errorMsg };
                      }

                      if (daysLeft <= 0 && userToLogin.role !== 'school_admin') {
                          return { user: null, error: 'Your school subscription has expired.' };
                      }
                  }
              }

              // Add login notification
              await this.addNotification({
                  user_id: userToLogin.id,
                  school_id: userToLogin.school_id,
                  title: 'Successful Login',
                  message: `You have successfully logged in to your account.`,
                  type: 'activity'
              });

              // Generate and save a secure session ID for single-device and active session tracking
              const newSessId = 'sess_' + Math.random().toString(36).substring(2) + '_' + Date.now();
              if (typeof window !== 'undefined') {
                  sessionStorage.setItem('ss_session_id', newSessId);
              }
              const nowIso = new Date().toISOString();
              await this.updateUser(userToLogin.id, { 
                  session_id: newSessId, 
                  last_active: nowIso 
              });
              
              userToLogin.session_id = newSessId;
              userToLogin.last_active = nowIso;

              return { user: userToLogin, error: null };
          }
      } catch (err: any) {
          return { user: null, error: err.message || 'Login failed' };
      }
      
      return { user: null, error: 'Login failed' };
  }

  async loginWithOTP(email: string) {
      const e = email.toLowerCase().trim();
      try {
          const { error } = await supabase.auth.signInWithOtp({
              email: e,
              options: {
                  shouldCreateUser: false,
              }
          });
          if (error) {
              return { success: false, error: error.message };
          }
          return { success: true, error: null };
      } catch (err: any) {
          return { success: false, error: err.message || 'OTP request failed' };
      }
  }

  async verifyOTP(email: string, token: string) {
      const e = email.toLowerCase().trim();
      const t = token.trim();
      try {
          const { data: authData, error: authError } = await supabase.auth.verifyOtp({
              email: e,
              token: t,
              type: 'email',
          });

          if (authError) {
              return { user: null, error: authError.message };
          }

          if (authData.user) {
              // Fetch user profile from our 'users' table
              const { data: userData, error: userError } = await supabase
                  .from('users')
                  .select('*')
                  .eq('id', authData.user.id)
                  .single();

              if (userError || !userData) {
                  return { user: null, error: 'User profile not found. Please contact support.' };
              }

              const userToLogin: User = userData;

              // Check user status
              if (userToLogin.status === 'inactive') {
                  return { user: null, error: 'Your user account is inactive. Please contact your administrator.' };
              }

              // Check school status for non-platform admins
              if (userToLogin.role !== 'platform_admin' && userToLogin.school_id) {
                  const { data: schoolData } = await supabase
                      .from('schools')
                      .select('*')
                      .eq('id', userToLogin.school_id)
                      .single();

                  if (schoolData) {
                      const isApproved = schoolData.status === 'active';
                      const getDaysRemaining = () => {
                          if(!schoolData.subscription_end_date) return 0;
                          const end = new Date(schoolData.subscription_end_date);
                          const now = new Date();
                          const diff = end.getTime() - now.getTime();
                          return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
                      };
                      const daysLeft = getDaysRemaining();
                      
                      if (!isApproved) {
                          let errorMsg = 'Your school account is not yet approved or is inactive.';
                          if (schoolData.status === 'pending' || schoolData.status === 'under_review' || schoolData.status === 'pending_payment') {
                              errorMsg = 'Your school registration is pending approval.';
                          } else if (schoolData.status === 'suspended') {
                              errorMsg = 'Your school account has been suspended.';
                          }
                          return { user: null, error: errorMsg };
                      }

                      if (daysLeft <= 0 && userToLogin.role !== 'school_admin') {
                          return { user: null, error: 'Your school subscription has expired.' };
                      }
                  }
              }

              // Add login notification
              await this.addNotification({
                  user_id: userToLogin.id,
                  school_id: userToLogin.school_id,
                  title: 'Successful Login (OTP)',
                  message: `You have successfully logged in to your account via OTP.`,
                  type: 'activity'
              });

              const newSessId = 'sess_' + Math.random().toString(36).substring(2) + '_' + Date.now();
              if (typeof window !== 'undefined') {
                  sessionStorage.setItem('ss_session_id', newSessId);
              }
              const nowIso = new Date().toISOString();
              await this.updateUser(userToLogin.id, { 
                  session_id: newSessId, 
                  last_active: nowIso 
              });
              
              userToLogin.session_id = newSessId;
              userToLogin.last_active = nowIso;

              return { user: userToLogin, error: null };
          }
      } catch (err: any) {
          return { user: null, error: err.message || 'OTP verification failed' };
      }
      return { user: null, error: 'OTP verification failed' };
  }

  // --- NOTIFICATIONS ---
  async getNotifications(userId: string): Promise<UserNotification[]> {
      if (!isSupabaseConfigured || !userId) {
          if (typeof window !== 'undefined' && userId) {
              const fallbackStr = localStorage.getItem(`ss_fallback_notifications_${userId}`);
              if (fallbackStr) {
                  try {
                      return JSON.parse(fallbackStr);
                  } catch (_) {}
              }
          }
          return [];
      }

      try {
          const { data, error } = await supabase
              .from('notifications')
              .select('*')
              .eq('user_id', userId)
              .order('created_at', { ascending: false });
          
          if (error) {
              console.warn('Error fetching notifications from Supabase:', error);
              if (typeof window !== 'undefined') {
                  const fallbackStr = localStorage.getItem(`ss_fallback_notifications_${userId}`);
                  if (fallbackStr) {
                      try {
                          return JSON.parse(fallbackStr);
                      } catch (_) {}
                  }
              }
              return [];
          }
          const formatted = (data || []).map(n => ({
              ...n,
              title: (n as any).title || 'Notification',
              type: (n as any).type || 'info',
              is_read: (n as any).is_read || false,
              school_id: (n as any).school_id || ''
          })) as UserNotification[];

          if (typeof window !== 'undefined') {
              try {
                  localStorage.setItem(`ss_fallback_notifications_${userId}`, JSON.stringify(formatted));
              } catch (_) {}
          }
          return formatted;
      } catch (err) {
          console.warn('getNotifications error:', err);
          if (typeof window !== 'undefined') {
              const fallbackStr = localStorage.getItem(`ss_fallback_notifications_${userId}`);
              if (fallbackStr) {
                  try {
                      return JSON.parse(fallbackStr);
                  } catch (_) {}
              }
          }
          return [];
      }
  }

  async addNotification(data: Omit<UserNotification, 'id' | 'created_at' | 'is_read'>, target?: { role?: Role; school_id?: string }): Promise<UserNotification[]> {
      const notifications: UserNotification[] = [];
      
      try {
          let userIds: string[] = [];
          
          if (data.user_id) {
              userIds.push(data.user_id);
          } else if (target) {
              // Broadcast to role or school
              const { data: users, error } = await supabase
                  .from('users')
                  .select('id')
                  .match({
                      ...(target.role ? { role: target.role } : {}),
                      ...(target.school_id ? { school_id: target.school_id } : {})
                  });
              
              if (!error && users) {
                  userIds = users.map(u => u.id);
              }
          }

          for (const uid of userIds) {
              const newNotification = {
                  ...data,
                  user_id: uid,
                  created_at: new Date().toISOString()
              };
              
              const res = await fetch('/api/notifications', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ payload: newNotification })
              });
              
              if (!res.ok) {
                  const errText = await res.text();
                  console.error('[DB] addNotification error:', errText);
              } else {
                  notifications.push(newNotification as any);
              }
          }
          
          // Dispatch event for real-time UI updates
          if (typeof window !== 'undefined' && notifications.length > 0) {
              window.dispatchEvent(new CustomEvent('notification_added'));
          }
      } catch (err) {
          console.error('addNotification error:', err);
      }
      return notifications;
  }

  async markNotificationAsRead(id: string) {
      try {
          const { error } = await supabase
              .from('notifications')
              .update({ is_read: true })
              .eq('id', id);
          if (error) throw error;
      } catch (err) {
          console.error('markNotificationAsRead error:', err);
      }
  }

  async markAllNotificationsAsRead(userId: string) {
      try {
          const { error } = await supabase
              .from('notifications')
              .update({ is_read: true })
              .eq('user_id', userId);
          if (error) throw error;
      } catch (err) {
          console.error('markAllNotificationsAsRead error:', err);
      }
  }

  async heartbeat(userId: string, currentPage?: string) {
      const nowIso = new Date().toISOString();
      
      let existingPrefs: any = {};
      try {
          const { data: userData } = await supabase.from('users').select('preferences').eq('id', userId).maybeSingle();
          existingPrefs = userData?.preferences || {};
      } catch (err) {
          console.warn("Failed to fetch preferences in heartbeat:", err);
      }
      
      // Get client metadata
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      let browser = "Chrome";
      if (ua.indexOf("Firefox") > -1) browser = "Firefox";
      else if (ua.indexOf("Safari") > -1 && ua.indexOf("Chrome") === -1) browser = "Safari";
      else if (ua.indexOf("Edge") > -1) browser = "Edge";
      else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) browser = "Opera";

      let os = "Windows";
      if (ua.indexOf("Mac") > -1) os = "MacOS";
      else if (ua.indexOf("Linux") > -1) os = "Linux";
      else if (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) os = "iOS";
      else if (ua.indexOf("Android") > -1) os = "Android";

      let deviceType = "Desktop";
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
          deviceType = "Mobile";
          if (/iPad|tablet/i.test(ua)) deviceType = "Tablet";
      }

      let ip = '127.0.0.1';
      let location = 'Local';
      
      if (typeof window !== 'undefined') {
          // @ts-ignore
          if (window.__cachedIP) {
              // @ts-ignore
              ip = window.__cachedIP;
              // @ts-ignore
              location = window.__cachedLocation || 'Local';
          } else {
              fetch('https://ipapi.co/json/')
                  .then(res => res.json())
                  .then(data => {
                      // @ts-ignore
                      window.__cachedIP = data.ip || '127.0.0.1';
                      // @ts-ignore
                      window.__cachedLocation = `${data.city || ''}, ${data.country_name || ''}`.trim() || 'Local';
                  })
                  .catch(() => {
                      fetch('https://api.ipify.org?format=json')
                          .then(res => res.json())
                          .then(data => {
                              // @ts-ignore
                              window.__cachedIP = data.ip || '127.0.0.1';
                              // @ts-ignore
                              window.__cachedLocation = 'Local';
                          })
                          .catch(() => {});
                  });
          }
      }

      const updatedPrefs = {
          ...existingPrefs,
          session_metadata: {
              ...(existingPrefs.session_metadata || {}),
              device_type: deviceType,
              browser,
              os,
              ip_address: ip,
              location,
              current_page: currentPage || 'Dashboard',
              login_time: existingPrefs.session_metadata?.login_time || nowIso
          }
      };

      await this.updateUser(userId, { 
          last_active: nowIso, 
          preferences: updatedPrefs 
      });
  }

  async validateSession(userId: string, sessionId: string): Promise<boolean> {
      const cacheKey = `sess_${userId}_${sessionId}`;
      const cached = this.getCache<boolean>(cacheKey);
      if (cached !== null) return cached;

      try {
          const { data: user } = await supabase.from('users').select('session_id, status, role, school_id').eq('id', userId).maybeSingle();
          if (!user) return false;
          if (user.status === 'inactive') return false;

          // Check school status if not platform_admin
          if (user.role !== 'platform_admin' && user.school_id) {
              const { data: school } = await supabase.from('schools').select('status').eq('id', user.school_id).maybeSingle();
              if (!school || school.status === 'inactive' || school.status === 'suspended') {
                  return false;
              }
          }

          if (!user.session_id) return true;
          const isValid = user.session_id === sessionId;
          this.setCache(cacheKey, isValid, 10000); // cache validation for 10s
          return isValid;
      } catch (err) {
          console.error("validateSession error:", err);
          return false;
      }
  }

  async verifyAdminCredentials(email: string, pass: string): Promise<boolean> {
      try {
          const { error } = await supabase.auth.signInWithPassword({
              email: email.toLowerCase().trim(),
              password: pass
          });
          return !error;
      } catch (err) {
          console.error("verifyAdminCredentials error:", err);
          return false;
      }
  }

  async checkUserExists(email: string): Promise<boolean> {
      const e = email.toLowerCase().trim();
      
      try {
          const { data: userData, error: userError } = await supabase
              .from('users')
              .select('id')
              .eq('email', e)
              .maybeSingle();
          if (userData) return true;
          
          const { data: schoolData, error: schoolError } = await supabase
              .from('schools')
              .select('id')
              .eq('admin_email', e)
              .maybeSingle();
          if (schoolData) return true;
      } catch (err) {
          console.error('Supabase checkUserExists failed:', err);
      }

      return false;
  }

  async checkSchoolNameExists(name: string, excludeId?: string): Promise<boolean> {
      const n = name.toLowerCase().trim();
      
      try {
          let query = supabase.from('schools').select('id').ilike('name', n);
          if (excludeId) query = query.neq('id', excludeId);
          const { data } = await query.maybeSingle();
          if (data) return true;
      } catch (err) {
          console.error('Supabase checkSchoolNameExists failed:', err);
      }

      return false;
  }

  async requestPasswordReset(email: string): Promise<{ success: boolean; link?: string }> {
      try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
              redirectTo: `${window.location.origin}/reset-password`,
          });
          if (error) throw error;
          return { success: true };
      } catch (err) {
          console.error('requestPasswordReset error:', err);
          return { success: false };
      }
  }

  async resetPassword(email: string, newPass: string): Promise<boolean> {
      try {
          const { error } = await supabase.auth.updateUser({ password: newPass });
          if (error) throw error;
          
          const { error: dbError } = await supabase
              .from('users')
              .update({ password: await this.hashPassword(newPass) })
              .eq('email', email.toLowerCase());
          
          return !dbError;
      } catch (err) {
          console.error('resetPassword error:', err);
          return false;
      }
  }

  async logout() {
      try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
              await this.terminateUserSession(user.id);
              await this.addNotification({
                  user_id: user.id,
                  title: 'Logged Out',
                  message: 'You have successfully logged out of your account.',
                  type: 'activity'
              });
          }
          if (typeof window !== 'undefined') {
              sessionStorage.removeItem('ss_session_id');
          }
          await supabase.auth.signOut();
          this.invalidateCache('current_user');
      } catch (e) {
          console.error("Logout error:", e);
      }
  }


  // --- SCHOOLS ---
  async getSchools(): Promise<SchoolData[]> {
      try {
          // Explicitly define columns to omit large columns like payment_proof
          const cols = 'id, name, sdms_code, status, created_at, admin_email, plan, district, sector, cell, phone, address, has_nursery, level, theme_color, logo, subscription_end_date, payment_method, student_count, subscription_price, user_id, settings';
          const { data, error } = await supabase
              .from('schools')
              .select(cols)
              .order('created_at', { ascending: false });

          if (error) {
              console.error('Error fetching schools from Supabase:', error);
              return [];
          }
          return (data || []).map(s => {
              const settings = (s as any).settings || {};
              return {
                ...s,
                stamp: (s as any).stamp || settings.stamp || '',
                logo: (s as any).logo || settings.logo || '',
                email: (s as any).email || '',
                academic_year: (s as any).academic_year || '',
                current_academic_year_id: (s as any).current_academic_year_id || '',
                payment_reference: (s as any).payment_reference || '',
                payment_date: (s as any).payment_date || '',
                payment_amount: (s as any).payment_amount || 0,
                rejection_reason: (s as any).rejection_reason || settings.rejection_reason || ''
              };
          }) as SchoolData[];
      } catch (e) {
          console.error('getSchools error:', e);
          return [];
      }
  }

  async getSchool(id: string): Promise<SchoolData | null> {
      const cacheKey = `school_${id}`;
      const cached = this.getCache<SchoolData>(cacheKey);
      if (cached) return cached;

      try {
          const { data, error } = await supabase
              .from('schools')
              .select('*')
              .eq('id', id)
              .single();
              
          if (error) {
              console.error('Error fetching school from Supabase:', error);
              return null;
          }
          const s = data;
          const settings = s.settings || {};
          
          const schoolObj = {
              ...s,
              stamp: (s as any).stamp || settings.stamp || '',
              logo: (s as any).logo || settings.logo || '',
              email: (s as any).email || '',
              academic_year: (s as any).academic_year || '',
              current_academic_year_id: (s as any).current_academic_year_id || '',
              payment_reference: (s as any).payment_reference || '',
              payment_date: (s as any).payment_date || '',
              payment_amount: (s as any).payment_amount || 0,
              rejection_reason: (s as any).rejection_reason || settings.rejection_reason || ''
          } as SchoolData;

          this.setCache(cacheKey, schoolObj, 20000); // cache for 20s
          return schoolObj;
      } catch (e) {
          console.error('getSchool error:', e);
          return null;
      }
  }

  // New method for Platform Dashboard
  async getUpcomingRenewals(): Promise<SchoolData[]> {
      const schools = await this.getSchools();
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
      
      return schools.filter(s => {
          if (!s.subscription_end_date || s.status !== 'active') return false;
          const expiry = new Date(s.subscription_end_date);
          return expiry > now && expiry <= thirtyDaysFromNow;
      }).sort((a,b) => new Date(a.subscription_end_date!).getTime() - new Date(b.subscription_end_date!).getTime());
  }

  // New method for Platform Activity Stream
  async getPlatformActivity() {
      // Mocking a live stream of events
      const actions = ['Login', 'Payment', 'Support Ticket', 'Registration', 'Report Generation'];
      const users = ['Head Teacher', 'System Admin', 'John Doe', 'Jane Smith', 'Bursar'];
      const schools = ['Kigali Intl', 'Bright Future', 'Green Hills', 'St. Marys'];
      
      const activity = [];
      for(let i=0; i<10; i++) {
          activity.push({
              id: `act_${i}`,
              action: actions[Math.floor(Math.random() * actions.length)],
              user: users[Math.floor(Math.random() * users.length)],
              school: schools[Math.floor(Math.random() * schools.length)],
              time: `${Math.floor(Math.random() * 59) + 1}m ago`,
              type: Math.random() > 0.8 ? 'alert' : 'normal'
          });
      }
      return activity;
  }

  async getSchoolTrackingStats() {
      try {
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const response = await fetch(`${baseUrl}/api/admin/school-tracking-stats`);
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to fetch school tracking stats');
          }
          return await response.json();
      } catch (e) {
          console.error('getSchoolTrackingStats error:', e);
          throw e;
      }
  }

  async registerSchool(data: any): Promise<{ success: boolean; error?: string }> { 
      if (!isSupabaseConfigured) {
          return { 
              success: false, 
              error: 'Supabase is not configured. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your environment variables in the Settings menu.' 
          };
      }

      try {
          const email = data.email.toLowerCase().trim();
          const password = data.pass.trim();
          
          // 0. Pre-registration checks (Professional safety)
          // Check if email is already in our 'users' table
          const { data: existingUser, error: existingUserError } = await supabase
              .from('users')
              .select('id')
              .eq('email', email)
              .maybeSingle();
          
          if (existingUser) {
              return { success: false, error: 'This email is already registered. If you have already submitted an application, please wait for approval or log in to check your status. Otherwise, please use a different email address.' };
          }

          // Check if school name already exists
          const { data: existingSchool } = await supabase
              .from('schools')
              .select('id')
              .eq('name', data.name)
              .maybeSingle();
          
          if (existingSchool) {
              return { success: false, error: 'A school with this name is already registered.' };
          }

          // Check if SDMS code already exists
          if (data.sdmsCode) {
              const { data: existingSdms } = await supabase
                  .from('schools')
                  .select('id')
                  .eq('sdms_code', data.sdmsCode)
                  .maybeSingle();
              
              if (existingSdms) {
                  return { success: false, error: 'SDMS code already exists. Please verify the school code.' };
              }
          }

          // 1. Generate School ID first
          const schoolId = 'sch_' + Math.random().toString(36).substring(2, 10);

          // 2. Create School Record FIRST (before auth.signUp)
          // This prevents "Database error saving new user" if a Supabase trigger
          // tries to insert the user profile with this school_id before the school exists.
          const newSchool = {
              id: schoolId,
              name: data.name,
              district: data.district,
              sector: data.sector || '',
              cell: data.cell || '',
              phone: data.phone || '',
              sdms_code: data.sdmsCode || '',
              has_nursery: !!data.hasNursery,
              plan: data.plan || 'dynamic',
              student_count: data.studentCount || 0,
              subscription_price: data.subscriptionPrice || 0,
              status: 'pending', 
              admin_email: email,
              payment_method: data.paymentMethod || 'other',
              payment_proof: data.paymentProof || '',
              user_id: null, // We will update this after auth.signUp
              created_at: new Date().toISOString()
          };

          const { data: schoolRecord, error: schoolError } = await supabase
              .from('schools')
              .insert([newSchool])
              .select()
              .single();

          if (schoolError || !schoolRecord) {
              console.error('School creation error:', schoolError);
              return { success: false, error: 'Failed to save school data: ' + (schoolError?.message || 'No school record returned') };
          }

          // 3. Create Auth User with metadata (role and school_id)
          const { data: authData, error: authError } = await supabase.auth.signUp({
              email: email,
              password: password,
              options: {
                  data: {
                      full_name: data.adminName,
                      role: 'school_admin',
                      school_id: schoolId
                  }
              }
          });

          if (authError) {
              // Rollback school creation if auth fails
              await supabase.from('schools').delete().eq('id', schoolId);
              
              if (authError.message.includes('already registered')) {
                  return { success: false, error: 'This email is already registered. If you have already submitted an application, please wait for approval or log in to check your status. Otherwise, please use a different email address.' };
              }
              return { success: false, error: authError.message };
          }

          const authUser = authData.user;
          if (!authUser) {
              await supabase.from('schools').delete().eq('id', schoolId);
              return { success: false, error: 'Failed to create user account. Please try again.' };
          }

          // Update school with the new user_id
          await supabase.from('schools').update({ user_id: authUser.id }).eq('id', schoolId);

          // 4. Create or Update User Profile in 'users' table
          const newUserProfile = {
              id: authUser.id,
              email: email,
              full_name: data.adminName,
              role: 'school_admin',
              school_id: schoolId,
              created_at: new Date().toISOString()
          };

          const { error: profileError } = await supabase
              .from('users')
              .upsert(newUserProfile, { onConflict: 'id' });

          if (profileError) {
              console.error('Profile creation error:', profileError);
              return { success: false, error: 'Auth account and school created, but failed to finalize user profile: ' + profileError.message };
          }

          // 5. Success!
          // Supabase signUp automatically logs the user in if email confirmation is disabled.
          return { success: true };
      } catch (e: any) {
          console.error("Registration error:", e);
          let errorMessage = e.message || 'An unexpected error occurred during registration.';
          
          if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
              errorMessage = 'Network error: Failed to connect to the database. This usually means the Supabase URL is incorrect or the service is unreachable. Please check your Supabase configuration in the Settings menu.';
          }
          
          return { success: false, error: errorMessage };
      }
  }

  async uploadFile(bucket: string, path: string, dataUrl: string): Promise<string> {
    try {
      // Create bucket if it doesn't exist (this might fail if not admin, but that's okay we'll try)
      // Usually buckets are created manually or via SQL migrations
      
      // Convert DataURL to Blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'upload.png', { type: blob.type });

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, { 
          upsert: true,
          contentType: blob.type
        });

      if (error) {
        // If bucket error, we might need to fallback to base64 or explain
        console.error('Upload error details:', error);
        throw error;
      }

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);

      return publicUrl;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  }

  async updateSchool(id: string, data: any) {
      try {
          // Proactive fallback for missing columns in some environments
          // If we detect stamp or rejection_reason, we ensure settings is updated too
          const school = await this.getSchool(id);
          if (school) {
              const currentSettings = (school as any).settings || {};
              let settingsUpdated = false;
              
              if (data.logo !== undefined) {
                  currentSettings.logo = data.logo;
                  settingsUpdated = true;
              }
              if (data.stamp !== undefined) {
                  currentSettings.stamp = data.stamp;
                  settingsUpdated = true;
              }
              if (data.rejection_reason !== undefined) {
                  currentSettings.rejection_reason = data.rejection_reason;
                  settingsUpdated = true;
              }
              
              if (settingsUpdated) {
                  data.settings = currentSettings;
              }
          }

          // Use backend API to bypass RLS for school updates (platform admin actions)
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const response = await fetch(`${baseUrl}/api/admin/update-school`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id, data })
          });
          
          if (!response.ok) {
              let errorData: any = {};
              try {
                  errorData = await response.json();
              } catch (e) {
                  errorData = { error: response.statusText };
              }
              
              const errStr = (errorData.error || errorData.message || "").toLowerCase();
              
              // If the error indicates missing column, retry without the problematic column
              if (errStr.includes('column "stamp"') || errStr.includes('column "rejection_reason"') || errStr.includes('could not find the \'stamp\'') || errStr.includes('could not find the \'rejection_reason\'')) {
                  console.warn('Database column missing, retrying with fallback to settings...');
                  
                  // Clone data and remove only the columns we suspect are missing
                  const retryData = { ...data };
                  if (errStr.includes('stamp')) delete (retryData as any).stamp;
                  if (errStr.includes('rejection_reason')) delete (retryData as any).rejection_reason;
                  // Handle case where logo might be missing in some environments too 
                  if (errStr.includes('logo')) delete (retryData as any).logo;
                  
                  const retryResponse = await fetch(`${baseUrl}/api/admin/update-school`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id, data: retryData })
                  });
                  
                  if (!retryResponse.ok) {
                      let retryError: any = {};
                      try {
                          retryError = await retryResponse.json();
                      } catch (e) {
                          retryError = { error: retryResponse.statusText };
                      }
                      throw new Error(retryError.error || retryError.message || 'Failed to update school on retry');
                  }
                  return;
              }
              
              throw new Error(errorData.error || errorData.message || 'Failed to update school');
          }
      } catch (err: any) {
          console.error('updateSchool error:', err);
          throw err;
      }
  }

  async deleteSchool(id: string) {
      try {
          // Use backend API to bypass RLS for school deletion (platform admin actions)
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const response = await fetch(`${baseUrl}/api/admin/delete-school`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id })
          });
          
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to delete school');
          }
      } catch (err) {
          console.error('deleteSchool error:', err);
          throw err;
      }
  }
  async updateSchoolStatus(id: string, status: string) {
      try {
          await this.updateSchool(id, { status });
      } catch (err) {
          console.error('updateSchoolStatus error:', err);
          throw err;
      }
  }
  async approveSchool(id: string) {
      await this.updateSchoolStatus(id, 'active');
  }
  async rejectSchool(id: string, reason: string) {
      try {
          await this.updateSchool(id, { status: 'rejected', rejection_reason: reason });
      } catch (err) {
          console.error('rejectSchool error:', err);
          throw err;
      }
  }
  async checkGlobalSDMSExists(sdmsCode: string, excludeId?: string): Promise<boolean> {
      if (!sdmsCode) return false;
      try {
          let sQuery = supabase.from('students').select('id').eq('sdms_code', sdmsCode);
          if (excludeId) sQuery = sQuery.neq('id', excludeId);
          const { data: sData } = await sQuery.maybeSingle();
          if (sData) return true;

          let uQuery = supabase.from('users').select('id').eq('sdms_code', sdmsCode);
          if (excludeId) uQuery = uQuery.neq('id', excludeId);
          const { data: uData } = await uQuery.maybeSingle();
          if (uData) return true;

          let schQuery = supabase.from('schools').select('id').eq('sdms_code', sdmsCode);
          if (excludeId) schQuery = schQuery.neq('id', excludeId);
          const { data: schData } = await schQuery.maybeSingle();
          if (schData) return true;
      } catch (err) {
          console.error("checkGlobalSDMSExists error:", err);
      }
      return false;
  }

  async checkSchoolSDMSExists(code: string, excludeId?: string): Promise<boolean> {
      if (!code) return false;
      try {
          let query = supabase.from('schools').select('id').eq('sdms_code', code);
          if (excludeId) query = query.neq('id', excludeId);
          const { data } = await query.maybeSingle();
          return !!data;
      } catch (err) {
          console.error("checkSchoolSDMSExists error:", err);
          return false;
      }
  }
  async checkEmailExists(email: string): Promise<boolean> {
    return this.checkUserExists(email);
  }

  // --- ACADEMIC YEARS ---
  async getAcademicYears(): Promise<AcademicYear[]> {
      const cacheKey = 'academic_years';
      const cached = this.getCache<AcademicYear[]>(cacheKey);
      if (cached) return cached;

      try {
          const { data, error } = await supabase.from('academic_years').select('*');
          if (error) {
              console.error('Error fetching academic years from Supabase:', error);
              return [];
          }
          const years = (data || []) as AcademicYear[];
          this.setCache(cacheKey, years, 30000); // cache for 30s
          return years;
      } catch (err) {
          console.error('getAcademicYears error:', err);
          return [];
      }
  }

  async createAcademicYear(data: any) {
      try {
          const id = data.id || `ay_${Date.now()}`;
          const { error } = await supabase.from('academic_years').insert([{ ...data, id }]);
          if (error) throw error;
          this.invalidateCache('academic_years');
      } catch (err) {
          console.error('createAcademicYear error:', err);
          throw err;
      }
  }

  async updateAcademicYear(id: string, data: Partial<AcademicYear>) {
      try {
          const { error } = await supabase.from('academic_years').update(data).eq('id', id);
          if (error) throw error;
          this.invalidateCache('academic_years');
      } catch (err) {
          console.error('updateAcademicYear error:', err);
          throw err;
      }
  }

  async deleteAcademicYear(id: string) {
      try {
          const { error } = await supabase.from('academic_years').delete().eq('id', id);
          if (error) throw error;
          this.invalidateCache('academic_years');
      } catch (err) {
          console.error('deleteAcademicYear error:', err);
          throw err;
      }
  }

  async closeAcademicYear(id: string) {
      await this.updateAcademicYear(id, { is_current: false });
  }

  // --- USERS & ROLES ---
  async getUsers(schoolId: string, role?: string): Promise<User[]> {
      try {
          let query = supabase.from('users').select('*');
          if (schoolId !== 'all') query = query.eq('school_id', schoolId);
          if (role) query = query.eq('role', role);
          
          const { data, error } = await query;
          if (error) {
              console.error('Error fetching users from Supabase:', error);
              return [];
          }
          return (data || []).map(u => {
              const prefs = u.preferences || {};
              return {
                  ...u,
                  avatar: u.avatar || prefs.avatar || '',
                  phone: u.phone || prefs.phone || '',
                  last_login: u.last_login || ''
              };
          }) as User[];
      } catch (e) {
          console.error('getUsers error:', e);
          return [];
      }
  }

  async addUser(data: any) {
      const email = data.email ? data.email.toLowerCase().trim() : '';
      if (email) {
          const { data: existingUser } = await supabase.from('users').select('id').eq('email', email).single();
          if (existingUser) {
              throw new Error('This email is already registered. Please use a different email.');
          }
      }

      try {
          console.log(`[DBService] Attempting to add user: ${email}, route: /api/admin/create-user`);
          const response = await this.fetchWithTimeout('/api/admin/create-user', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  email: email,
                  password: data.password ? data.password.trim() : 'password123',
                  full_name: data.full_name,
                  role: data.role || 'teacher',
                  school_id: data.school_id,
                  sdms_code: data.sdms_code,
                  assignments: data.assignments || []
              })
          });

          console.log(`[DBService] Received response status: ${response.status}`);
          const text = await response.text();
          let result;
          try {
              result = JSON.parse(text);
          } catch (e) {
              console.error('Failed to parse JSON. Status:', response.status, 'Response text:', text);
              throw new Error(`Server returned an invalid response (create, status ${response.status}). This usually means the backend server is not running or is misconfigured.`);
          }

          if (!response.ok) {
              throw new Error(result.error || 'Failed to create user account.');
          }
          return { ...data, id: result.user.id };
      } catch (err: any) {
          console.error('Error in addUser:', err);
          if (err.message.includes('Failed to fetch') || err.message.includes('Load failed')) {
              throw new Error('Could not connect to the backend server. Please ensure the development server is running.');
          }
          throw err;
      }
  }

  async inviteUser(data: any) {
      const email = data.email ? data.email.toLowerCase().trim() : '';
      if (email) {
          const { data: existingUser } = await supabase.from('users').select('id').eq('email', email).single();
          if (existingUser) {
              throw new Error('This email is already registered. Please use a different email.');
          }
      }

      try {
          const response = await this.fetchWithTimeout('/api/admin/invite-user', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  email: email,
                  full_name: data.full_name,
                  role: data.role || 'teacher',
                  school_id: data.school_id,
                  sdms_code: data.sdms_code,
                  assignments: data.assignments || []
              })
          });

          const text = await response.text();
          let result;
          try {
              result = JSON.parse(text);
          } catch (e) {
              console.error('Failed to parse JSON. Status:', response.status, 'Response text:', text);
              throw new Error(`Server returned an invalid response (invite, status ${response.status}). This usually means the backend server is not running or is misconfigured.`);
          }

          if (!response.ok) {
              throw new Error(result.error || 'Failed to invite user.');
          }
          return { ...data, id: result.user.id };
      } catch (err: any) {
          console.error('Error in inviteUser:', err);
          if (err.message.includes('Failed to fetch') || err.message.includes('Load failed')) {
              throw new Error('Could not connect to the backend server. Please ensure the development server is running.');
          }
          throw err;
      }
  }

  async createTeacher(data: any) {
      return this.addUser({ ...data, role: 'teacher' });
  }

  async createHeadteacher(data: any) {
      return this.addUser({ ...data, role: 'school_admin' });
  }

  async createUser(data: any) {
      return this.addUser(data);
  }

  async updateUser(id: string, data: any) {
      // Sanitize updates
      const updates: any = {};
      const allowedCols = ['email', 'full_name', 'role', 'school_id', 'status', 'sdms_code', 'assignments', 'academic_level', 'assigned_class', 'assigned_subject', 'job_title', 'preferences', 'session_id', 'last_active'];
      
      for (const col of allowedCols) {
          if (data[col] !== undefined) {
              updates[col] = data[col];
          }
      }

      if (data.phone !== undefined || data.avatar !== undefined) {
          const { data: userData } = await supabase.from('users').select('preferences').eq('id', id).single();
          const existingPrefs = userData?.preferences || {};
          updates.preferences = {
              ...existingPrefs,
              ...updates.preferences, // use provided preferences if any
              ...(data.phone !== undefined && { phone: data.phone }),
              ...(data.avatar !== undefined && { avatar: data.avatar })
          };
      }

      const { error } = await supabase
          .from('users')
          .update(updates)
          .eq('id', id);
      
      if (error) {
          console.error('Error updating user in Supabase:', error);
          throw error;
      }

      this.invalidateCache('current_user');
  }

  async deleteUser(id: string) {
      try {
          const response = await this.fetchWithTimeout('/api/admin/delete-user', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ id })
          });

          const text = await response.text();
          let result;
          try {
              result = JSON.parse(text);
          } catch (e) {
              console.error('Failed to parse JSON. Response text:', text);
              throw new Error(`Server returned an invalid response (delete, status ${response.status}).`);
          }

          if (!response.ok) {
              throw new Error(result.error || 'Failed to fully clean and delete user.');
          }

          return { success: true };
      } catch (err: any) {
          console.error('Error in deleteUser:', err);
          throw err;
      }
  }

  async updateCurrentUser(id: string, data: any) {
      if (data.password) {
          const { error } = await supabase.auth.updateUser({ password: data.password });
          if (error) throw error;
      }
      
      const tableData = { ...data };
      delete tableData.password;
      
      if (Object.keys(tableData).length > 0) {
          await this.updateUser(id, tableData);
      }
  }

  async verifyPassword(id: string, passwordAttempt: string): Promise<boolean> {
      try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user?.email) {
              const { error } = await supabase.auth.signInWithPassword({
                  email: session.user.email,
                  password: passwordAttempt.trim()
              });
              return !error;
          }
          return false;
      } catch (e) {
          return false;
      }
  }

  // --- STUDENTS & PARENTS ---
  async getAllStudents(): Promise<Student[]> {
      try {
          const { data, error } = await supabase.from('students').select('*');
          
          console.log(`[DB] getAllStudents:`, {
              count: data?.length || 0,
              error: error?.message
          });

          if (error) {
              console.error('Error fetching all students from Supabase:', error);
              return [];
          }
          return (data || []).map(s => ({
              ...s,
              className: (s as any).class_grade || '',
              academic_level: (s as any).academic_level || 'primary',
              status: (s as any).status || 'active',
              email: (s as any).email || '',
              phone: (s as any).phone || '',
              address: (s as any).address || '',
              date_of_birth: (s as any).date_of_birth || '',
              guardian_name: (s as any).guardian_name || '',
              guardian_phone: (s as any).guardian_phone || '',
              avatar: (s as any).avatar || ''
          })) as Student[];
      } catch (err) {
          console.error('getAllStudents error:', err);
          return [];
      }
  }

  async getStudents(schoolId: string, classId?: string, level?: string, className?: string): Promise<Student[]> {
      try {
          let query = supabase
              .from('students')
              .select('*')
              .eq('school_id', schoolId);

          if (classId && classId !== 'All Classes' && classId !== 'all') {
              console.log(`[DB] getStudents filtering by classId/Name: ${classId}, className: ${className}`);
              
              // Resiliently resolve BOTH ID and Name using classes list
              const classesList = await this.getClasses(schoolId);
              const classObj = classesList.find(c => c.id === classId || c.name === classId);
              const resolvedId = classObj ? classObj.id : classId;
              const resolvedName = classObj ? classObj.name : (className || classId);
              
              console.log(`[DB] Resolved class filter - ID: ${resolvedId}, Name: ${resolvedName}`);
              query = query.or(`class_grade.eq."${resolvedName}",stream.eq."${resolvedName}",class_grade.eq."${resolvedId}",stream.eq."${resolvedId}"`);
          }

          if (level) {
              query = query.eq('academic_level', level.toLowerCase());
          }

          const { data, error } = await query;
          if (error) {
              console.error('[DB] getStudents error:', error);
              throw error;
          }

          return (data || []).map(s => ({
              ...s,
              className: (s as any).class_grade || '',
              academic_level: (s as any).academic_level || 'primary',
              status: (s as any).status || 'active',
              email: (s as any).email || '',
              phone: (s as any).phone || '',
              address: (s as any).address || '',
              date_of_birth: (s as any).date_of_birth || '',
              guardian_name: (s as any).guardian_name || '',
              guardian_phone: (s as any).guardian_phone || '',
              avatar: (s as any).avatar || ''
          })) as Student[];
      } catch (err) {
          console.error('getStudents error:', err);
          return [];
      }
  }

  async checkStudentSDMSExists(sdmsCode: string, excludeStudentId?: string): Promise<boolean> {
      return this.checkGlobalSDMSExists(sdmsCode, excludeStudentId);
  }

  async checkStudentIndexExists(indexNumber: string, excludeStudentId?: string): Promise<boolean> {
      if (!indexNumber) return false;
      try {
          let sQuery = supabase.from('students').select('id').eq('index_number', indexNumber);
          if (excludeStudentId) sQuery = sQuery.neq('id', excludeStudentId);
          const { data } = await sQuery.maybeSingle();
          if (data) return true;
      } catch (err) {
          console.error("checkStudentIndexExists error:", err);
      }
      return false;
  }

  async checkTeacherSDMSExists(sdmsCode: string, excludeTeacherId?: string): Promise<boolean> {
      return this.checkGlobalSDMSExists(sdmsCode, excludeTeacherId);
  }

  async generateUniqueSDMS(): Promise<string> {
      let code = '';
      let exists = true;
      let attempts = 0;
      while (exists && attempts < 10) {
          code = Math.floor(100000000000 + Math.random() * 900000000000).toString();
          exists = await this.checkGlobalSDMSExists(code);
          attempts++;
      }
      return code;
  }

  async generateUniqueIndex(): Promise<string> {
      let code = '';
      let exists = true;
      let attempts = 0;
      const year = new Date().getFullYear().toString();
      while (exists && attempts < 10) {
          code = year + Math.floor(10000000 + Math.random() * 90000000).toString();
          exists = await this.checkStudentIndexExists(code);
          attempts++;
      }
      return code;
  }

  async addStudent(data: any): Promise<{ success: boolean; error?: string; student?: Student }> { 
      const studentId = data.id || `st_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      
      // Auto-generate missing identifiers
      if (!data.sdms_code) {
          data.sdms_code = await this.generateUniqueSDMS();
      }
      if (!data.index_number) {
          data.index_number = await this.generateUniqueIndex();
      }

      const newStudent: any = {
          id: studentId,
          full_name: data.full_name,
          school_id: data.school_id,
          academic_level: data.academic_level,
          class_grade: data.class_grade,
          stream: data.stream,
          sdms_code: data.sdms_code,
          gender: data.gender,
          dob: data.dob,
          parent_id: data.parent_id,
          index_number: data.index_number,
          created_at: new Date().toISOString()
      };

      try {
          const { error } = await supabase.from('students').insert([newStudent]);
          if (error) {
              console.error('Supabase addStudent failed:', error);
              return { success: false, error: error.message };
          }
          return { success: true, student: newStudent };
      } catch (err: any) {
          console.error('Supabase addStudent error:', err);
          return { success: false, error: err.message };
      }
  }

  async updateStudent(id: string, data: any) {
      // Sanitize updates to only include existing columns
      const updates: any = {};
      const allowedCols = ['full_name', 'school_id', 'academic_level', 'class_grade', 'stream', 'sdms_code', 'gender', 'dob', 'parent_id', 'index_number'];
      
      for (const col of allowedCols) {
          if (data[col] !== undefined) {
              updates[col] = data[col];
          }
      }

      const { error } = await supabase
          .from('students')
          .update(updates)
          .eq('id', id);
      if (error) {
          console.error('Supabase updateStudent failed:', error);
          throw error;
      }
  }

  async promoteStudent(studentId: string, targetClass: string, targetYear: string) {
      try {
          await this.updateStudent(studentId, { class_grade: targetClass });
      } catch (error) {
          console.error("Failed to promote student:", error);
          throw error;
      }
  }

  async deleteStudent(id: string) {
      // Clean up expected reference tables beforehand to bypass foreign key constraints
      const relatedTables = [
          'attendance',
          'course_marks',
          'assessment_scores',
          'exam_records',
          'marks'
      ];
      
      for (const table of relatedTables) {
          try {
              const { error: deletionError } = await supabase.from(table).delete().eq('student_id', id);
              // Ignore errors completely if the table or column doesn't exist, as not every school uses every module
          } catch (e) {
              // Fail silently for child tables
          }
      }

      const { data, error } = await supabase
          .from('students')
          .delete()
          .eq('id', id)
          .select();
          
      console.log("DELETE STUDENT RESULT:", data);

      if (error) {
          console.error('Supabase deleteStudent failed:', error);
          throw error;
      }
      
      if (!data || data.length === 0) {
          throw new Error('Delete failed: No rows deleted (RLS or invalid ID)');
      }
  }

  async batchEnrollStudents(schoolId: string, studentsList: any[]): Promise<{added: number, duplicates: number, error?: string}> { 
      const processedStudents = [];
      let addedCount = 0;
      let duplicateCount = 0;

      // Extract unique provided SDMS and Index numbers to perform single batch query check
      const providedSDMS = studentsList.map(s => String(s.sdms_code || '').trim()).filter(Boolean);
      const providedIndexes = studentsList.map(s => String(s.index_number || '').trim()).filter(Boolean);

      const existingSDMS = new Set<string>();
      const existingIndexes = new Set<string>();

      try {
          if (providedSDMS.length > 0) {
              // 1. Check in students table
              const { data: stdSDMS } = await supabase
                  .from('students')
                  .select('sdms_code')
                  .in('sdms_code', providedSDMS);
              stdSDMS?.forEach(row => existingSDMS.add(String(row.sdms_code).trim()));

              // 2. Check in users table
              const { data: usrSDMS } = await supabase
                  .from('users')
                  .select('sdms_code')
                  .in('sdms_code', providedSDMS);
              usrSDMS?.forEach(row => existingSDMS.add(String(row.sdms_code).trim()));

              // 3. Check in schools table
              const { data: schSDMS } = await supabase
                  .from('schools')
                  .select('sdms_code')
                  .in('sdms_code', providedSDMS);
              schSDMS?.forEach(row => existingSDMS.add(String(row.sdms_code).trim()));
          }

          if (providedIndexes.length > 0) {
              const { data: stdIndexes } = await supabase
                  .from('students')
                  .select('index_number')
                  .in('index_number', providedIndexes);
              stdIndexes?.forEach(row => existingIndexes.add(String(row.index_number).trim()));
          }
      } catch (err) {
          console.error('[DBService] Bulk precheck failed, falling back to empty sets:', err);
      }

      // Track newly generated codes during this batch to prevent inner-batch duplicates
      const generatedSDMSThisBatch = new Set<string>();
      const generatedIndexesThisBatch = new Set<string>();

      for (const s of studentsList) {
          const studentId = s.id || `st_batch_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
          const sCode = String(s.sdms_code || '').trim();
          const iNum = String(s.index_number || '').trim();

          // Check duplicates against DB and in-batch generated
          if (sCode) {
              if (existingSDMS.has(sCode) || generatedSDMSThisBatch.has(sCode)) {
                  duplicateCount++;
                  continue;
              }
          }
          if (iNum) {
              if (existingIndexes.has(iNum) || generatedIndexesThisBatch.has(iNum)) {
                  duplicateCount++;
                  continue;
              }
          }

          // Generate or assign SDMS
          let finalSDMS = sCode;
          if (!finalSDMS) {
              // Generate locally using high-entropy random sequence (12 digits)
              let attempts = 0;
              while (attempts < 10) {
                  const candidate = Math.floor(100000000000 + Math.random() * 900000000000).toString();
                  if (!existingSDMS.has(candidate) && !generatedSDMSThisBatch.has(candidate)) {
                      finalSDMS = candidate;
                      generatedSDMSThisBatch.add(candidate);
                      break;
                  }
                  attempts++;
              }
              if (!finalSDMS) finalSDMS = Math.floor(100000000000 + Math.random() * 900000000000).toString();
          }

          // Generate or assign Index Number
          let finalIndex = iNum;
          if (!finalIndex) {
              const year = new Date().getFullYear().toString();
              let attempts = 0;
              while (attempts < 10) {
                  const candidate = year + Math.floor(10000000 + Math.random() * 90000000).toString();
                  if (!existingIndexes.has(candidate) && !generatedIndexesThisBatch.has(candidate)) {
                      finalIndex = candidate;
                      generatedIndexesThisBatch.add(candidate);
                      break;
                  }
                  attempts++;
              }
              if (!finalIndex) finalIndex = year + Math.floor(10000000 + Math.random() * 90000000).toString();
          }

          const student = {
              id: studentId,
              full_name: s.full_name,
              school_id: schoolId,
              academic_level: s.academic_level,
              class_grade: s.class_grade,
              stream: s.stream,
              sdms_code: finalSDMS,
              gender: s.gender,
              dob: s.dob || null,
              parent_id: s.parent_id || null,
              index_number: finalIndex,
              created_at: new Date().toISOString()
          };
          processedStudents.push(student);
          addedCount++;
      }

      if (processedStudents.length > 0) {
          try {
              const { error } = await supabase.from('students').insert(processedStudents);
              if (error) {
                  console.error('Supabase batchEnrollStudents failed:', error);
                  return { added: 0, duplicates: duplicateCount, error: error.message };
              }
          } catch (err: any) {
              console.error('Supabase batchEnrollStudents error:', err);
              return { added: 0, duplicates: duplicateCount, error: err.message };
          }
      }
      
      return { added: addedCount, duplicates: duplicateCount }; 
  }
  
  async getParents(schoolId: string): Promise<Parent[]> { return []; }

  // --- CLASSES & SUBJECTS ---
  async getClasses(schoolId: string, level?: string, academicYearId?: string): Promise<ClassGrade[]> {
      const cacheKey = `classes_${schoolId}_${level || ''}_${academicYearId || ''}`;
      const cached = this.getCache<ClassGrade[]>(cacheKey);
      if (cached) return cached;

      try {
          let query = supabase
              .from('classes')
              .select('*')
              .eq('school_id', schoolId);

          const { data, error } = await query;
          if (error) {
              console.error('Error fetching classes from Supabase:', error);
              return [];
          }
          
          let allClasses = (data || []).map(c => ({
              ...c,
              level: (c as any).level || 'Primary',
              academic_year_id: (c as any).academic_year_id || '',
              teacher_id: (c as any).teacher_id || null,
              streams: (c as any).streams || []
          })) as ClassGrade[];

          // Fetch class teachers from teacher_assignments
          let assignmentQuery = supabase
              .from('teacher_assignments')
              .select('class_id, teacher_id')
              .eq('school_id', schoolId)
              .is('subject_id', null);

          if (academicYearId) {
              // assignmentQuery = assignmentQuery.eq('academic_year_id', academicYearId);
              // academic_year_id is missing from teacher_assignments table, skipping filter
          }

          const { data: assignments } = await assignmentQuery;
          
          if (assignments) {
              allClasses = allClasses.map(c => {
                  const assignment = assignments.find(a => a.class_id === c.id);
                  return { ...c, teacher_id: assignment ? assignment.teacher_id : null };
              });
          }

          if (level) {
              const normalizedLevel = level.toLowerCase();
              allClasses = allClasses.filter(c => {
                  if (c.academic_level && c.academic_level !== 'all') {
                      return c.academic_level.toLowerCase() === normalizedLevel;
                  }
                  const name = c.name.toLowerCase();
                  const isNurseryName = name.includes('baby') || name.includes('middle') || name.includes('top') || name.includes('nursery') || name.startsWith('n');
                  const isPrimaryName = name.startsWith('p') || name.includes('primary') || name.includes('grade') || name.includes('class') || name.includes('year');
                  const isSecondaryName = name.startsWith('s') || name.includes('senior') || name.includes('secondary') || name.includes('form') || name.includes('high');
                  
                  if (normalizedLevel === 'nursery') {
                      if (isNurseryName) return true;
                      if (isPrimaryName || isSecondaryName) return false;
                      return true;
                  }
                  if (normalizedLevel === 'primary') {
                      if (isPrimaryName) return true;
                      if (isNurseryName || isSecondaryName) return false;
                      return true;
                  }
                  if (normalizedLevel === 'secondary') {
                      if (isSecondaryName) return true;
                      if (isNurseryName || isPrimaryName) return false;
                      return true;
                  }
                  return true;
              });
          }

          this.setCache(cacheKey, allClasses, 10000); // Cache for 10s
          return allClasses;
      } catch (err) {
          console.error('Supabase getClasses error:', err);
          return [];
      }
  }
  
  async addClass(data: any) {
      const classId = `c_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      const newClass = { 
          id: classId, 
          name: data.name,
          school_id: data.school_id,
          academic_level: data.academic_level,
          created_at: new Date().toISOString() 
      };

      try {
          const { error } = await supabase.from('classes').insert([newClass]);
          if (error) {
              console.error('Supabase addClass failed:', error);
              throw error;
          }
          this.invalidateCache('classes_');
          return newClass;
      } catch (e) {
          console.error('addClass error:', e);
          throw e;
      }
  }

  async batchAddClasses(classes: any[]) {
      const newClasses = classes.map(c => ({
          name: c.name,
          school_id: c.school_id,
          academic_level: c.academic_level || 'primary',
          id: `c_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          created_at: new Date().toISOString()
      }));

      try {
          const { error } = await supabase.from('classes').insert(newClasses);
          if (error) {
              console.error('Supabase batchAddClasses failed:', error);
              throw error;
          }
          this.invalidateCache('classes_');
      } catch (e) {
          console.error('batchAddClasses error:', e);
          throw e;
      }
  }

  async updateClass(id: string, updates: Partial<ClassGrade>) {
      try {
          // Sanitize updates
          const sanitized: any = {};
          const allowed = ['name', 'school_id', 'academic_level'];
          for (const key of allowed) {
              if ((updates as any)[key] !== undefined) {
                  sanitized[key] = (updates as any)[key];
              }
          }

          const { error } = await supabase
              .from('classes')
              .update(sanitized)
              .eq('id', id);
          if (error) {
              console.error('Supabase updateClass failed:', error);
              throw error;
          }
          this.invalidateCache('classes_');
      } catch (e) {
          console.error('updateClass error:', e);
          throw e;
      }
  }

  async updateClassTeacher(classId: string, className: string, teacherId: string | null, schoolId: string, academicYearId: string) {
      try {
          // 1. Find current class teacher assignment in table
          const { data: currentAssignments } = await supabase
              .from('teacher_assignments')
              .select('*')
              .eq('class_id', classId)
              .is('subject_id', null);
          
          const oldTeacherId = currentAssignments && currentAssignments.length > 0 ? currentAssignments[0].teacher_id : null;

          // 2. If old teacher exists and is different, remove their assignment from profile and table
          if (oldTeacherId && oldTeacherId !== teacherId) {
              const { data: oldTeacher } = await supabase.from('users').select('*').eq('id', oldTeacherId).single();
              if (oldTeacher) {
                  const newAssignments = (oldTeacher.assignments || []).filter((a: any) => 
                      !(a.class_name === className && a.subject_name === 'Class Teacher')
                  );
                  await this.updateUser(oldTeacherId, { assignments: newAssignments });
                  await this.syncTeacherAssignments(oldTeacherId, schoolId, academicYearId);
              } else {
                  // If teacher profile not found, at least remove from table
                  const toRemove = currentAssignments!.filter(a => a.teacher_id === oldTeacherId);
                  await Promise.all(toRemove.map(a => this.removeTeacherAssignment(a.id)));
              }
          }

          // 3. If new teacher is provided, add their assignment to profile and table
          if (teacherId) {
              const { data: newTeacher } = await supabase.from('users').select('*').eq('id', teacherId).single();
              if (newTeacher) {
                  const assignments = newTeacher.assignments || [];
                  const exists = assignments.some((a: any) => a.class_name === className && a.subject_name === 'Class Teacher');
                  
                  if (!exists) {
                      assignments.push({ class_name: className, subject_name: 'Class Teacher' });
                      await this.updateUser(teacherId, { assignments });
                      await this.syncTeacherAssignments(teacherId, schoolId, academicYearId);
                  }
              }
          }
          this.invalidateCache('classes_');
      } catch (e) {
          console.error('updateClassTeacher error:', e);
      }
  }

  async getClassStudentCount(classId: string): Promise<number> {
      try {
          const { count, error } = await supabase
              .from('students')
              .select('*', { count: 'exact', head: true })
              .eq('class_grade', classId);
              // .neq('status', 'deleted'); // status column is missing
          if (error) {
              console.error('Error counting students:', error);
              return 0;
          }
          return count || 0;
      } catch (e) {
          console.error('getClassStudentCount error:', e);
          return 0;
      }
  }

  async deleteClass(id: string): Promise<{ success: boolean; error?: string }> {
      const studentCount = await this.getClassStudentCount(id);
      if (studentCount > 0) {
          return { 
              success: false, 
              error: `Cannot delete class. There are ${studentCount} students enrolled in this class. Please move or remove the students first.` 
          };
      }

      try {
          const { error } = await supabase.from('classes').delete().eq('id', id);
          if (error) throw error;
          this.invalidateCache('classes_');
          return { success: true };
      } catch (err: any) {
          return { success: false, error: err.message || 'Failed to delete class' };
      }
  }

  async setClassStatus(id: string, status: 'active' | 'inactive'): Promise<{ success: boolean; error?: string }> {
      // Status column does not exist on classes table
      return { success: true };
  }
  
  async getSubjects(schoolId: string, includeInactive: boolean = false, includeNurseryCategories: boolean = false): Promise<Subject[]> {
      const cacheKey = `subjects_${schoolId}_${includeInactive}_${includeNurseryCategories}`;
      const cached = this.getCache<Subject[]>(cacheKey);
      if (cached) return cached;

      console.log("SCHOOL ID:", schoolId);
      let allSubjects: Subject[] = [];

      try {
          let query = supabase
              .from('subjects')
              .select('*')
              .eq('school_id', schoolId);

          const { data, error } = await query;
          
          console.log("SUPABASE RESPONSE:", data, error);

          if (error) {
              console.error('Error fetching subjects from Supabase:', error);
          } else if (data) {
              allSubjects = (data || []).map(s => ({
                  ...s,
                  code: (s as any).code || '',
                  category: (s as any).category || 'Core',
                  max_marks: (s as any).max_marks || 100,
                  is_nursery: (s as any).is_nursery || false,
                  description: (s as any).description || '',
                  status: (s as any).status || 'active',
                  teacher_id: (s as any).teacher_id || null
              })) as Subject[];
          }
      } catch (err) {
          console.error('Error fetching subjects from Supabase:', err);
      }

      if (includeNurseryCategories) {
          const nurserySkills = await this.getNurserySkills(schoolId);
          const categories = Array.from(new Set(nurserySkills.map(s => s.category)));
          categories.forEach(cat => {
              if (!allSubjects.find(s => s.name.toLowerCase() === cat.toLowerCase())) {
                  allSubjects.push({
                      id: `cat_${cat}`,
                      name: cat,
                      code: cat.substring(0, 4).toUpperCase(),
                      school_id: schoolId,
                      status: 'active'
                  } as Subject);
              }
          });
      }

      this.setCache(cacheKey, allSubjects, 10000); // cache for 10s
      return allSubjects;
  }
  
  async addSubject(data: any) {
      const subjectId = `s_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      try {
          const newSubject = {
              id: subjectId,
              name: data.name,
              school_id: data.school_id,
              created_at: new Date().toISOString()
          };
          const { error } = await supabase.from('subjects').insert([newSubject]);
          if (error) throw error;
          this.invalidateCache('subjects_');
      } catch (err) {
          console.error('addSubject error:', err);
          throw err;
      }
  }

  async batchAddSubjects(subjects: any[]) {
      const newSubjects = subjects.map(s => ({
          id: `s_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          name: s.name,
          school_id: s.school_id,
          created_at: new Date().toISOString()
      }));
      
      try {
          const { error } = await supabase.from('subjects').insert(newSubjects);
          if (error) throw error;
          this.invalidateCache('subjects_');
      } catch (e) {
          console.error('batchAddSubjects error:', e);
          throw e;
      }
  }

  async updateSubject(id: string, updates: Partial<Subject>) {
      try {
          // Sanitize updates
          const sanitized: any = {};
          const allowed = ['name', 'school_id'];
          for (const key of allowed) {
              if ((updates as any)[key] !== undefined) {
                  sanitized[key] = (updates as any)[key];
              }
          }

          const { error } = await supabase
              .from('subjects')
              .update(sanitized)
              .eq('id', id);
          if (error) throw error;
          this.invalidateCache('subjects_');
      } catch (e) {
          console.error('updateSubject error:', e);
          throw e;
      }
  }

  async deleteSubject(id: string) {
      try {
          const { error } = await supabase
              .from('subjects')
              .delete()
              .eq('id', id);
          if (error) throw error;
          this.invalidateCache('subjects_');
      } catch (e) {
          console.error('deleteSubject error:', e);
          throw e;
      }
  }

  // --- LESSONS ---
  async getLessons(subjectId: string): Promise<Lesson[]> {
      try {
          const { data, error } = await supabase
              .from('lessons')
              .select('*')
              .eq('subject_id', subjectId)
              .order('order', { ascending: true });
          
          if (error) throw error;
          return data as Lesson[];
      } catch (e) {
          console.error('getLessons error:', e);
          return [];
      }
  }

  async addLesson(data: Partial<Lesson>) {
      const id = `l_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      try {
          const { error } = await supabase
              .from('lessons')
              .insert([{ ...data, id, created_at: new Date().toISOString() }]);
          if (error) throw error;
          return { success: true, id };
      } catch (e) {
          console.error('addLesson error:', e);
          throw e;
      }
  }

  async updateLesson(id: string, updates: Partial<Lesson>) {
      try {
          const { error } = await supabase
              .from('lessons')
              .update(updates)
              .eq('id', id);
          if (error) throw error;
          return { success: true };
      } catch (e) {
          console.error('updateLesson error:', e);
          throw e;
      }
  }

  async deleteLesson(id: string) {
      try {
          const { error } = await supabase
              .from('lessons')
              .delete()
              .eq('id', id);
          if (error) throw error;
          return { success: true };
      } catch (e) {
          console.error('deleteLesson error:', e);
          throw e;
      }
  }

    /**
     * Gets all lessons assigned to a teacher for a specific academic year.
     */
    async getTeacherLessons(teacherId: string, academicYearId: string): Promise<any[]> {
        try {
            const { data, error } = await supabase
                .from('teacher_lessons')
                .select(`
                    id,
                    status,
                    lesson:lessons (
                        id,
                        name,
                        code,
                        description,
                        order,
                        subject:subjects (
                            id,
                            name
                        )
                    )
                `)
                .eq('teacher_id', teacherId)
                .eq('academic_year_id', academicYearId);
            
            if (error) {
                console.warn("getTeacherLessons error (ignoring if table missing):", error.message);
                return [];
            }
            return data || [];
        } catch (e) {
            return [];
        }
    }

    /**
     * Saves attendance records and creates a notification.
     */
    async saveAttendance(records: any[], userId: string, schoolId: string, className: string): Promise<void> {
        try {
            const date = new Date().toISOString().split('T')[0];
            const formattedRecords = records.map(r => ({
                id: `att_${r.studentId}_${date}`,
                school_id: schoolId,
                student_id: r.studentId,
                date: date,
                status: r.status,
                // class_name, sub_status, time, reason, marked_by are missing from table
            }));

            const { error } = await supabase
                .from('attendance')
                .upsert(formattedRecords, { onConflict: 'student_id,date' });

            if (error) throw error;
            
            await this.addNotification({
                user_id: userId,
                school_id: schoolId,
                title: 'Attendance Marked',
                message: `You have successfully marked attendance for ${className}.`,
                type: 'activity'
            });
        } catch (e) {
            console.error('saveAttendance error:', e);
            throw e;
        }
    }

    async getAttendanceByDate(schoolId: string, date: string): Promise<any[]> {
        try {
            const { data, error } = await supabase
                .from('attendance')
                .select(`
                    *,
                    student:students(id, full_name, admission_number, class_name)
                `)
                .eq('school_id', schoolId)
                .eq('date', date);
            
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getAttendanceByDate error:', e);
            return [];
        }
    }

    async getAttendanceByStudent(studentId: string): Promise<any[]> {
        try {
            const { data, error } = await supabase
                .from('attendance')
                .select('*')
                .eq('student_id', studentId)
                .order('date', { ascending: false });
            
            if (error) throw error;
            return data || [];
        } catch (e) {
            console.error('getAttendanceByStudent error:', e);
            return [];
        }
    }

  // --- FINANCE ---
  async getFeeStructures(schoolId: string): Promise<FeeStructure[]> {
      try {
          const { data, error } = await supabase
              .from('fee_structures')
              .select('*')
              .eq('school_id', schoolId);
          if (error) {
              console.error('Error fetching fee structures from Supabase:', error);
              return [];
          }
          return data as FeeStructure[];
      } catch (e) {
          console.error('getFeeStructures error:', e);
          return [];
      }
  }

  async addFeeStructure(data: any) {
      try {
          const { error } = await supabase.from('fee_structures').insert([data]);
          if (error) throw error;
      } catch (e) {
          console.error('addFeeStructure error:', e);
          throw e;
      }
  }

  async deleteFeeStructure(id: string) {
      try {
          const { error } = await supabase
              .from('fee_structures')
              .delete()
              .eq('id', id);
          if (error) throw error;
      } catch (e) {
          console.error('deleteFeeStructure error:', e);
          throw e;
      }
  }

  async getStudentPayments(schoolId: string): Promise<FeePayment[]> {
      try {
          const { data, error } = await supabase
              .from('fee_payments')
              .select('*')
              .eq('school_id', schoolId);
          if (error) {
              console.error('Error fetching student payments from Supabase:', error);
              return [];
          }
          return data as FeePayment[];
      } catch (e) {
          console.error('getStudentPayments error:', e);
          return [];
      }
  }

  async addStudentPayment(data: any) {
      try {
          const { error } = await supabase.from('fee_payments').insert([data]);
          if (error) throw error;
      } catch (e) {
          console.error('addStudentPayment error:', e);
          throw e;
      }
  }
  
  async getFiscalYears() { return [2023, 2024, 2025]; }
  
  async getFinancialMetrics(year: number, scope: string) {
      const schools = await this.getSchools();
      
      const planPrices: Record<string, number> = {
          basic: 30000,
          pro: 60000,
          enterprise: 90000
      };

      let totalLiq = 0;
      let mrr = 0;
      let onboarding = 0;
      let unverifiedCount = 0;
      let unverifiedAmount = 0;
      let pendingAmount = 0;
      let rejectedAmount = 0;
      let rejectedCount = 0;
      
      const now = new Date();
      const ledger: Payment[] = [];
      
      schools.forEach((school, index) => {
          // Calculate MRR (only for active schools)
          const planPrice = planPrices[school.plan || 'basic'] || 30000;
          
          if (school.status === 'active') {
              mrr += planPrice;
          } else if (school.status === 'pending_payment' || school.status === 'under_review') {
              unverifiedCount++;
              unverifiedAmount += planPrice;
              pendingAmount += planPrice;
          } else if (school.status === 'rejected') {
              rejectedCount++;
              rejectedAmount += planPrice;
          }
          
          // Calculate total liquidity (assume they paid for the months they've been active)
          const createdDate = school.created_at ? new Date(school.created_at) : now;
          // Calculate months difference
          let monthsActive = (now.getFullYear() - createdDate.getFullYear()) * 12 + (now.getMonth() - createdDate.getMonth());
          if (monthsActive < 1) monthsActive = 1; // At least 1 month
          
          if (school.status === 'active') {
              totalLiq += (planPrice * monthsActive);
              onboarding += 50000; // Assume a one-time onboarding fee
              totalLiq += 50000;
              
              // Add to ledger for history
              ledger.push({
                  id: `tx_paid_${school.id}_${index}`,
                  school_id: school.id,
                  school_name: school.name,
                  amount: planPrice,
                  status: 'paid',
                  date: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
                  type: 'subscription',
                  currency: 'RWF',
                  invoice_id: `INV-${year}-${1000+index}`,
                  method: 'momo'
              });
          } else if (school.status === 'pending_payment' || school.status === 'under_review') {
              ledger.push({
                  id: `tx_pending_${school.id}_${index}`,
                  school_id: school.id,
                  school_name: school.name,
                  amount: planPrice,
                  status: 'pending',
                  date: now.toISOString(),
                  type: 'subscription',
                  currency: 'RWF',
                  invoice_id: `INV-PEND-${year}-${1000+index}`,
                  method: 'momo'
              });
          } else if (school.status === 'rejected') {
              ledger.push({
                  id: `tx_rej_${school.id}_${index}`,
                  school_id: school.id,
                  school_name: school.name,
                  amount: planPrice,
                  status: 'rejected',
                  date: now.toISOString(),
                  type: 'subscription',
                  currency: 'RWF',
                  invoice_id: `INV-REJ-${year}-${1000+index}`,
                  method: 'card'
              });
          }
      });

      // Calculate a simple growth metric based on active schools vs total
      const growth = schools.length > 0 ? Math.round((schools.filter(s => s.status === 'active').length / schools.length) * 100) : 0;

      return {
          liquidity: totalLiq,
          mrr: mrr,
          onboarding: onboarding,
          unverified: { count: unverifiedCount, amount: unverifiedAmount },
          pending: { count: unverifiedCount, amount: pendingAmount },
          rejected: { count: rejectedCount, amount: rejectedAmount },
          growth: growth > 0 ? growth : 15, // Fallback to 15% if no data
          ledger: ledger.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      };
  }

  async getPlans(): Promise<Plan[]> {
      try {
          const { data, error } = await supabase.from('plans').select('*');
          if (error) {
              console.error('Error fetching plans from Supabase:', error);
              // Return defaults if table doesn't exist or error
              return [
                  { id: 'starter', name: 'Starter', price_monthly: 0, price_yearly: 30000, description: 'Basic', features: ['Up to 100 Students', 'Basic Reports', 'Email Support'] },
                  { id: 'professional', name: 'Professional', price_monthly: 0, price_yearly: 60000, description: 'Pro', features: ['Up to 500 Students', 'Finance Module', 'Priority Support'], is_popular: true },
                  { id: 'enterprise', name: 'Enterprise', price_monthly: 0, price_yearly: 90000, description: 'Unlimited', features: ['Unlimited Students', 'API Access', 'Dedicated Account Manager'] }
              ];
          }
          return data as Plan[];
      } catch (err) {
          console.error('getPlans error:', err);
          return [];
      }
  }

  async savePlans(plans: Plan[]) {
      try {
          // This is a batch operation, we might want to delete and insert or upsert
          // For simplicity, we'll assume we're updating existing ones or inserting new ones
          const { error } = await supabase.from('plans').upsert(plans);
          if (error) throw error;
      } catch (err) {
          console.error('savePlans error:', err);
      }
  }

  // --- COMMUNICATION ---

  async getAnnouncements(schoolId: string): Promise<Announcement[]> {
      try {
          let query = supabase.from('announcements').select('*');
          if (schoolId !== 'all') {
              query = query.or(`school_id.eq.${schoolId},school_id.eq.all`);
          }
          const { data, error } = await query.order('date', { ascending: false });
          if (error) {
              console.error('Error fetching announcements from Supabase:', error);
              return [];
          }
          return data as Announcement[];
      } catch (err) {
          console.error('getAnnouncements error:', err);
          return [];
      }
  }

  async addAnnouncement(data: any) {
      try {
          const newAnn = { ...data, id: data.id || `ann_${Date.now()}`, date: new Date().toISOString() };
          const { error } = await supabase.from('announcements').insert([newAnn]);
          if (error) throw error;
      } catch (err) {
          console.error('addAnnouncement error:', err);
          throw err;
      }
  }

  async updateAnnouncement(id: string, data: any) {
      try {
          const { error } = await supabase
              .from('announcements')
              .update(data)
              .eq('id', id);
          if (error) throw error;
      } catch (err) {
          console.error('updateAnnouncement error:', err);
          throw err;
      }
  }

  async deleteAnnouncement(id: string) {
      try {
          const { error } = await supabase
              .from('announcements')
              .delete()
              .eq('id', id);
          if (error) throw error;
      } catch (err) {
          console.error('deleteAnnouncement error:', err);
          throw err;
      }
  }
  
  async getMaterials(schoolId: string): Promise<Material[]> { return []; }
  async getGuides(schoolId: string, role?: string): Promise<Guide[]> { return []; }
  async addGuide(data: any) {}
  async updateGuide(id: string, data: any) {}
  async deleteGuide(id: string) {}
  
  async getSupportTickets(userId: string, role?: string, schoolId?: string): Promise<SupportTicket[]> {
      try {
          const params = new URLSearchParams();
          if (userId) params.append('userId', userId);
          if (role) params.append('role', role);
          if (schoolId) params.append('schoolId', schoolId);

          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const response = await fetch(`${baseUrl}/api/support-tickets?${params.toString()}`);
          if (!response.ok) throw new Error('Failed to fetch tickets via API');
          const result = await response.json();
          const data = result.data || [];

          return data.map((t: any) => {
              try {
                  if (t.message && t.message.startsWith('{')) {
                      const parsed = JSON.parse(t.message);
                      return {
                          ...t,
                          ...parsed,
                          id: t.id,
                          user_id: t.user_id,
                          school_id: t.school_id,
                          subject: t.subject,
                          created_at: t.created_at
                      };
                  }
              } catch (e) {
                  // Fallback
              }
              return {
                  ...t,
                  status: t.status || 'open',
                  category: t.category || 'technical',
                  priority: t.priority || 'medium',
                  ticket_number: t.ticket_number || t.id,
                  responses: t.responses || []
              };
          });
      } catch (err) {
          console.error('getSupportTickets error:', err);
          return [];
      }
  }

  async createSupportTicket(data: any) {
      try {
          const payload = {
              id: data.id,
              user_id: data.user_id,
              school_id: data.school_id,
              subject: data.subject,
              message: JSON.stringify({
                  message: data.message,
                  status: data.status,
                  category: data.category,
                  priority: data.priority,
                  ticket_number: data.ticket_number,
                  responses: data.responses,
                  updated_at: data.updated_at
              })
          };
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const response = await fetch(`${baseUrl}/api/support-tickets`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ payload })
          });
          const resText = await response.text();
          if (!response.ok) throw new Error(resText || 'Failed to create ticket via API');
      } catch (err) {
          console.error('createSupportTicket error:', err);
          throw err;
      }
  }

  async updateTicketStatus(ticketId: string, status: string) {
      try {
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const res = await fetch(`${baseUrl}/api/support-tickets/${ticketId}/status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status })
          });
          const resText = await res.text();
          if (!res.ok) throw new Error(resText || 'Failed to update ticket status via API');
      } catch (err) {
          console.error('updateTicketStatus error:', err);
          throw err;
      }
  }

  async addTicketResponse(ticketId: string, response: any) {
      try {
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const res = await fetch(`${baseUrl}/api/support-tickets/${ticketId}/responses`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ response })
          });
          const resText = await res.text();
          if (!res.ok) throw new Error(resText || 'Failed to add ticket response via API');
      } catch (err) {
          console.error('addTicketResponse error:', err);
          throw err;
      }
  }
  async getGlobalBanner() { return { message: '', active: false }; }

  // --- SESSIONS ---
  async getActiveSessions(schoolId: string, includeEnded = false): Promise<ActiveSession[]> {
      const users = await this.getUsers(schoolId);
      const now = Date.now();
      const inactivityLimit = 3 * 60 * 1000; // 3 minutes for high accuracy online status

      const sessions: ActiveSession[] = [];
      
      for (const u of users) {
          const meta = (u.preferences as any)?.session_metadata;
          if (!u.session_id && !meta) {
              continue;
          }

          const hasActiveSession = u.session_id && u.session_id !== 'terminated' && u.last_active;
          const lastActiveTime = (hasActiveSession && u.last_active) ? new Date(u.last_active).getTime() : 0;
          const isOnlineNow = hasActiveSession && (now - lastActiveTime < inactivityLimit);

          if (!includeEnded && !isOnlineNow) {
              continue;
          }

          let status: 'active' | 'idle' | 'ended' = 'ended';
          if (hasActiveSession) {
              status = isOnlineNow ? 'active' : 'idle';
          }

          let schoolName = 'Platform Admin';
          if (u.school_id !== 'platform') {
              const school = await this.getSchool(u.school_id);
              schoolName = school ? school.name : 'Unknown School';
          }

          const sessionMeta = meta || {};

          sessions.push({
              id: u.session_id || 'terminated',
              user_id: u.id,
              user_name: u.full_name,
              user_role: u.role,
              user_avatar: u.avatar,
              school_id: u.school_id,
              school_name: schoolName,
              ip_address: sessionMeta.ip_address || '127.0.0.1',
              device_type: sessionMeta.device_type || 'Desktop',
              browser: sessionMeta.browser || 'Chrome',
              os: sessionMeta.os || 'Windows',
              last_active: u.last_active || 'Never',
              login_time: sessionMeta.login_time || u.last_active || 'Never',
              location: sessionMeta.location || 'Local',
              status: status,
              current_page: sessionMeta.current_page || 'Dashboard'
          });
      }
      return sessions;
  }

  async terminateSession(sessionId: string) {
      const users = await this.getUsers('all');
      const user = users.find(u => u.session_id === sessionId);
      if (user) {
          await this.updateUser(user.id, { session_id: 'terminated', last_active: null });
      }
  }

  async terminateUserSession(userId: string) {
      await this.updateUser(userId, { session_id: 'terminated', last_active: null });
  }

  async terminateAllSessions(schoolId: string) {
      const users = await this.getUsers(schoolId);
      for (const u of users) {
          if (u.session_id && u.session_id !== 'terminated') {
              await this.updateUser(u.id, { session_id: 'terminated', last_active: null });
          }
      }
  }

  // --- ACADEMICS & MARKS ---

  // --- ACADEMICS & MARKS ---
  // getTeacherCourses is now defined above


  async getCourseMarks(courseId: string): Promise<MarkRecord[]> {
      try {
          const { data, error } = await supabase
              .from('course_marks')
              .select('*')
              .eq('course_id', courseId);
          
          if (error) {
              console.error('Error fetching course marks from Supabase:', error);
              return [];
          }
          
          return data as MarkRecord[];
      } catch (err) {
          console.error('getCourseMarks error:', err);
          return [];
      }
  }


  async saveCourseMarks(courseId: string, marks: MarkRecord[]) {
      try {
          // 1. Save to course_marks table
          const { error: marksError } = await supabase.from('course_marks').upsert(
              marks.map(m => ({ ...m, course_id: courseId }))
          );
          if (marksError) throw marksError;

          // 2. Sync to exam_records and assessments for Report Card
          const parts = courseId.split('_');
          const subjectName = parts[1];
          
          const subjects = await this.getSubjects('all');
          const subject = subjects.find(s => s.name === subjectName);
          const subjectId = subject ? subject.id : 's1';
          const term = 'Term 1';

          // Sync Exam Records (Term Score)
          const examRecords = marks.map(m => ({
              id: `er_${m.student_id}_${subjectId}_${term}`,
              student_id: m.student_id,
              student_name: m.student_name,
              subject_id: subjectId,
              subject_name: subjectName,
              term: term,
              score: m.term_score || 0,
              max_score: 50,
              status: m.status || 'draft',
              academic_year_id: 'ay_2024'
          }));
          
          const { error: examError } = await supabase.from('exam_records').upsert(examRecords);
          if (examError) throw examError;

          // 3. Sync Unit Scores (AssessmentScores)
          // Ensure "End of Unit" assessment exists
          const { data: assessments, error: fetchAssError } = await supabase
              .from('assessments')
              .select('*')
              .eq('subject_id', subjectId)
              .eq('name', 'End of Unit')
              .eq('term', term);
          
          if (fetchAssError) throw fetchAssError;

          let unitAss = assessments?.[0];
          if (!unitAss) {
              const newAss = {
                  id: `ass_unit_${subjectId}_${term}`,
                  name: 'End of Unit',
                  type: 'test',
                  term: term,
                  school_id: 'sch_demo',
                  academic_year_id: 'ay_2024',
                  class_id: 'c3',
                  subject_id: subjectId,
                  max_score: 50,
                  weight_percentage: 50
              };
              const { data: createdAss, error: createAssError } = await supabase
                  .from('assessments')
                  .insert([newAss])
                  .select()
                  .single();
              
              if (createAssError) throw createAssError;
              unitAss = createdAss;
          }

          const unitScores = marks.map(m => ({
              id: `as_${m.student_id}_${unitAss.id}`,
              assessment_id: unitAss.id,
              student_id: m.student_id,
              score: m.unit_score || 0
          }));
          
          const { error: scoresError } = await supabase.from('assessment_scores').upsert(unitScores);
          if (scoresError) throw scoresError;

      } catch (err) {
          console.error('saveCourseMarks error:', err);
          throw err;
      }
  }
  
  async getGradingScheme(schoolId: string): Promise<GradingScheme[]> {
      if (!isSupabaseConfigured || !schoolId) {
          return [
              { id: 'g1', school_id: schoolId, min_score: 80, max_score: 100, grade_label: 'A', color_code: '#10b981', remarks: 'Excellent' },
              { id: 'g2', school_id: schoolId, min_score: 70, max_score: 79, grade_label: 'B', color_code: '#3b82f6', remarks: 'Very Good' },
              { id: 'g3', school_id: schoolId, min_score: 60, max_score: 69, grade_label: 'C', color_code: '#f59e0b', remarks: 'Good' },
              { id: 'g4', school_id: schoolId, min_score: 50, max_score: 59, grade_label: 'D', color_code: '#f97316', remarks: 'Pass' },
              { id: 'g5', school_id: schoolId, min_score: 0, max_score: 49, grade_label: 'F', color_code: '#ef4444', remarks: 'Fail' }
          ];
      }

      try {
          const { data, error } = await supabase
              .from('grading_schemes')
              .select('*')
              .eq('school_id', schoolId)
              .order('min_score', { ascending: false });
          
          if (error) {
              console.warn('Error fetching grading scheme, returning defaults:', error.message);
          } else if (data && data.length > 0) {
              return data as GradingScheme[];
          }
          
          // Default scheme if none found
          return [
              { id: 'g1', school_id: schoolId, min_score: 80, max_score: 100, grade_label: 'A', color_code: '#10b981', remarks: 'Excellent' },
              { id: 'g2', school_id: schoolId, min_score: 70, max_score: 79, grade_label: 'B', color_code: '#3b82f6', remarks: 'Very Good' },
              { id: 'g3', school_id: schoolId, min_score: 60, max_score: 69, grade_label: 'C', color_code: '#f59e0b', remarks: 'Good' },
              { id: 'g4', school_id: schoolId, min_score: 50, max_score: 59, grade_label: 'D', color_code: '#f97316', remarks: 'Pass' },
              { id: 'g5', school_id: schoolId, min_score: 0, max_score: 49, grade_label: 'F', color_code: '#ef4444', remarks: 'Fail' }
          ];
      } catch (err) {
          console.warn('getGradingScheme error:', err);
          return [];
      }
  }
  
  async saveGradingScheme(schoolId: string, scheme: GradingScheme[]) {
      try {
          const { error } = await supabase
              .from('grading_schemes')
              .upsert(scheme.map(s => ({ ...s, school_id: schoolId })));
          if (error) throw error;
      } catch (err) {
          console.error('saveGradingScheme error:', err);
          throw err;
      }
  }
  
  async getAssessmentConfigs(schoolId: string): Promise<AssessmentTypeConfig[]> { return []; }
  
  async getAssessments(schoolId: string, term: string): Promise<Assessment[]> { 
      try {
          const { data, error } = await supabase
              .from('assessments')
              .select('*')
              .eq('school_id', schoolId)
              .eq('term', term);
          if (error) {
              console.error('Error fetching assessments from Supabase:', error);
              return [];
          }
          return data as Assessment[];
      } catch (err) {
          console.error('getAssessments error:', err);
          return [];
      }
  }
  
  async addAssessment(data: any) {
      try {
          const id = data.id || `ass_${Date.now()}`;
          const { error } = await supabase.from('assessments').insert([{ ...data, id }]);
          if (error) throw error;
      } catch (err) {
          console.error('addAssessment error:', err);
          throw err;
      }
  }
  
  async getAssessmentScores(assessmentId: string): Promise<AssessmentScore[]> { 
      try {
          const { data, error } = await supabase
              .from('assessment_scores')
              .select('*')
              .eq('assessment_id', assessmentId);
          if (error) {
              console.error('Error fetching assessment scores from Supabase:', error);
              return [];
          }
          return data as AssessmentScore[];
      } catch (err) {
          console.error('getAssessmentScores error:', err);
          return [];
      }
  }
  
  async saveAssessmentScores(scores: AssessmentScore[]) {
      try {
          const { error } = await supabase
              .from('assessment_scores')
              .upsert(scores);
          if (error) throw error;
      } catch (err) {
          console.error('saveAssessmentScores error:', err);
          throw err;
      }
  }
  
  // --- NURSERY & REPORT CARD MOCKS ---
  async getNurserySkills(schoolId: string): Promise<NurserySkill[]> {
      const { data, error } = await supabase
          .from('nursery_skills')
          .select('*')
          .eq('school_id', schoolId);
      if (error) {
          console.error('Error fetching nursery skills from Supabase:', error);
          return [];
      }
      return data as NurserySkill[];
  }

  async addNurserySkill(skill: NurserySkill): Promise<void> {
      const { error } = await supabase.from('nursery_skills').insert([skill]);
      if (error) throw error;
  }

  async updateNurserySkill(skill: NurserySkill): Promise<void> {
      try {
          const { error } = await supabase
              .from('nursery_skills')
              .update(skill)
              .eq('id', skill.id);
          if (error) throw error;
      } catch (err) {
          console.error('updateNurserySkill error:', err);
          throw err;
      }
  }

  async deleteNurserySkill(skillId: string): Promise<void> {
      try {
          const { error } = await supabase
              .from('nursery_skills')
              .delete()
              .eq('id', skillId);
          if (error) throw error;
      } catch (err) {
          console.error('deleteNurserySkill error:', err);
          throw err;
      }
  }

  async resetNurserySkills(schoolId: string): Promise<void> {
      const defaultSkills = [
          { id: `sk_${Date.now()}_1`, category: 'Ikinyarwanda', name: 'Ikinyarwanda', school_id: schoolId },
          { id: `sk_${Date.now()}_2`, category: 'English', name: 'English', school_id: schoolId },
          { id: `sk_${Date.now()}_3`, category: 'Imibare', name: 'Imibare', school_id: schoolId },
          { id: `sk_${Date.now()}_4`, category: 'Ubugeni n\'Umuco', name: 'Ubugeni n\'Umuco', school_id: schoolId },
          { id: `sk_${Date.now()}_5`, category: 'Imyitwarire n\'iterambere mu mbamutima', name: 'Imyitwarire n\'iterambere mu mbamutima', school_id: schoolId },
          { id: `sk_${Date.now()}_6`, category: 'Ibonezabuzima', name: 'Ibonezabuzima', school_id: schoolId },
          { id: `sk_${Date.now()}_7`, category: 'Ubumenyi bw\'Ibidukikije', name: 'Ubumenyi bw\'Ibidukikije', school_id: schoolId }
      ];

      try {
          // Delete all existing skills for this school
          const { error: deleteError } = await supabase
              .from('nursery_skills')
              .delete()
              .eq('school_id', schoolId);
          if (deleteError) throw deleteError;

          // Insert default skills
          const { error: insertError } = await supabase
              .from('nursery_skills')
              .insert(defaultSkills);
          if (insertError) throw insertError;
      } catch (err) {
          console.error('resetNurserySkills error:', err);
          throw err;
      }
  }

  async getNurseryEvaluations(schoolId: string, studentId: string, term: string): Promise<NurseryEvaluation[]> {
      try {
          const { data, error } = await supabase
              .from('nursery_evaluations')
              .select('*')
              .eq('student_id', studentId)
              .eq('term', term);
          if (error) {
              console.error('Error fetching nursery evaluations from Supabase:', error);
              return [];
          }
          return data as NurseryEvaluation[];
      } catch (err) {
          console.error('getNurseryEvaluations error:', err);
          return [];
      }
  }

  async getNurseryEvaluationsByClass(schoolId: string, classId: string, term: string, academicYearId: string): Promise<NurseryEvaluation[]> {
      try {
          const students = await this.getStudents(schoolId, classId);
          const studentIds = students.map(s => s.id);
          if (studentIds.length === 0) return [];

          const { data, error } = await supabase
              .from('nursery_evaluations')
              .select('*')
              .in('student_id', studentIds)
              .eq('term', term);
          if (error) {
              console.error('Error fetching nursery evaluations from Supabase:', error);
              return [];
          }
          return data as NurseryEvaluation[];
      } catch (err) {
          console.error('getNurseryEvaluationsByClass error:', err);
          return [];
      }
  }

  async getAllNurseryEvaluations(schoolId: string, studentId: string): Promise<NurseryEvaluation[]> {
      try {
          const { data, error } = await supabase
              .from('nursery_evaluations')
              .select('*')
              .eq('student_id', studentId);
          if (error) {
              console.error('Error fetching all nursery evaluations from Supabase:', error);
              return [];
          }
          return data as NurseryEvaluation[];
      } catch (err) {
          console.error('getAllNurseryEvaluations error:', err);
          return [];
      }
  }

  async getNurseryEvaluationsBySchool(schoolId: string): Promise<NurseryEvaluation[]> {
      try {
          // We need to join with students to filter by school_id, 
          // or just fetch students first
          const students = await this.getStudents(schoolId);
          const studentIds = students.map(s => s.id);
          if (studentIds.length === 0) return [];

          const { data, error } = await supabase
              .from('nursery_evaluations')
              .select('*')
              .in('student_id', studentIds);
          if (error) {
              console.error('Error fetching nursery evaluations by school from Supabase:', error);
              return [];
          }
          return data as NurseryEvaluation[];
      } catch (err) {
          console.error('getNurseryEvaluationsBySchool error:', err);
          return [];
      }
  }

  async saveNurseryEvaluations(evaluations: NurseryEvaluation[]) {
      try {
          const { error } = await supabase
              .from('nursery_evaluations')
              .upsert(evaluations);
          if (error) throw error;
      } catch (err) {
          console.error('saveNurseryEvaluations error:', err);
          throw err;
      }
  }

  async getNurseryReportMeta(studentId: string, term: string): Promise<any> {
      try {
          const { data, error } = await supabase
              .from('nursery_meta')
              .select('*')
              .eq('student_id', studentId)
              .eq('term', term)
              .maybeSingle();
          if (error) {
              console.error('Error fetching nursery meta from Supabase:', error);
              return null;
          }
          return data;
      } catch (err) {
          console.error('getNurseryReportMeta error:', err);
          return null;
      }
  }

  async getAllNurseryReportMeta(studentId: string): Promise<any[]> {
      try {
          const { data, error } = await supabase
              .from('nursery_meta')
              .select('*')
              .eq('student_id', studentId);
          if (error) {
              console.error('Error fetching all nursery meta from Supabase:', error);
              return [];
          }
          return data;
      } catch (err) {
          console.error('getAllNurseryReportMeta error:', err);
          return [];
      }
  }

  async saveNurseryReportMeta(meta: any) {
      try {
          const id = meta.id || `nm_${Date.now()}`;
          const { error } = await supabase
              .from('nursery_meta')
              .upsert([{ ...meta, id }]);
          if (error) throw error;
      } catch (err) {
          console.error('saveNurseryReportMeta error:', err);
          throw err;
      }
  }
  

    async getStudentMarks(schoolId: string, studentId: string, term?: string, academicYearId?: string): Promise<ExamRecord[]> {
        try {
            let query = supabase
                .from('exam_records')
                .select('*')
                .eq('school_id', schoolId)
                .eq('student_id', studentId);
            
            if (term) query = query.eq('term', term);
            if (academicYearId) query = query.eq('academic_year_id', academicYearId);
            
            const { data, error } = await query;
            if (error) {
                console.error('Error fetching student exam records from Supabase:', error);
                return [];
            }
            
            return (data || []).map(r => ({
                ...r,
                type: (r as any).type || 'exam',
                test_score: (r as any).test_score !== undefined && (r as any).test_score !== null ? (r as any).test_score : null,
                exam_score: (r as any).exam_score !== undefined && (r as any).exam_score !== null ? (r as any).exam_score : null,
                color: (r as any).color || '#000000',
                teacher_name: (r as any).teacher_name || ''
            })) as ExamRecord[];
        } catch (err) {
            console.error('getStudentMarks error:', err);
            return [];
        }
    }

    async getExamRecords(schoolId: string, className?: string, subjectId?: string, term?: string, academicYearId?: string): Promise<ExamRecord[]> { 
        try {
            console.log(`[DB] getExamRecords: schoolId=${schoolId}, className/classId=${className}, subjectId=${subjectId}, term=${term}, academicYearId=${academicYearId}`);
            let allData: any[] = [];
            let from = 0;
            const limit = 1000;
            let hasMore = true;
            
            let studentIds: string[] = [];
            if (className) {
                // If className is actually a class ID, resolve it to a name using the faster, cached getClasses method
                const classesList = await this.getClasses(schoolId);
                const classObj = classesList.find(c => c.id === className || c.name === className);
                const resolvedClassName = classObj ? classObj.name : className;
                const resolvedClassId = classObj ? classObj.id : className;
                
                const students = await this.getStudents(schoolId, resolvedClassId, undefined, resolvedClassName);
                studentIds = students.map(s => s.id);
                if (studentIds.length === 0) {
                    console.log(`[DB] getExamRecords returned 0 students for class ${className}`);
                    return [];
                }
            }

            while (hasMore) {
                let query = supabase
                    .from('exam_records')
                    .select('*')
                    .eq('school_id', schoolId)
                    .range(from, from + limit - 1);
                
                if (subjectId) query = query.eq('subject_id', subjectId);
                if (term) query = query.eq('term', term);
                if (academicYearId) query = query.eq('academic_year_id', academicYearId);
                if (className && studentIds.length > 0) {
                    query = query.in('student_id', studentIds);
                }

                const { data, error } = await query;
                if (error) {
                    console.error('Error fetching exam records from Supabase:', error);
                    return [];
                }

                if (data && data.length > 0) {
                    allData = [...allData, ...data];
                    if (data.length < limit) {
                        hasMore = false;
                    } else {
                        from += limit;
                    }
                } else {
                    hasMore = false;
                }
            }
            
            let records = allData.map(r => ({
                ...r,
                type: (r as any).type || 'exam',
                test_score: (r as any).test_score !== undefined && (r as any).test_score !== null ? (r as any).test_score : null,
                exam_score: (r as any).exam_score !== undefined && (r as any).exam_score !== null ? (r as any).exam_score : null,
                color: (r as any).color || '#000000',
                teacher_name: (r as any).teacher_name || ''
            })) as ExamRecord[];
            
            // Deduplicate duplicate records in database: keep the newest/most complete/submitted record
            const uniqueMap: Record<string, ExamRecord> = {};
            records.forEach(r => {
                const key = `${r.student_id}_${r.subject_id}_${r.term}_${r.academic_year_id}`;
                const existing = uniqueMap[key];
                if (existing) {
                    const keepNew = 
                        ((r.status === 'submitted' || r.status === 'approved') && (existing.status !== 'submitted' && existing.status !== 'approved')) ||
                        ((r.score !== null && r.score !== undefined && r.score !== '') && (existing.score === null || existing.score === undefined || existing.score === '')) ||
                        (new Date((r as any).created_at || 0) > new Date((existing as any).created_at || 0));
                    
                    if (keepNew) {
                        uniqueMap[key] = r;
                    }
                } else {
                    uniqueMap[key] = r;
                }
            });
            records = Object.values(uniqueMap);
            
            return records;
        } catch (err) {
            console.error('getExamRecords error:', err);
            return [];
        }
    }

    async getExamRecordsByStudentIds(schoolId: string, studentIds: string[], academicYearId?: string): Promise<ExamRecord[]> {
        try {
            if (!studentIds || studentIds.length === 0) return [];
            
            let allData: any[] = [];
            let from = 0;
            const limit = 1000;
            let hasMore = true;

            while (hasMore) {
                let query = supabase
                    .from('exam_records')
                    .select('*')
                    .eq('school_id', schoolId)
                    .in('student_id', studentIds)
                    .range(from, from + limit - 1);
                
                if (academicYearId) query = query.eq('academic_year_id', academicYearId);
                
                const { data, error } = await query;
                if (error) {
                    console.error('Error fetching exam records by student IDs:', error);
                    return [];
                }
                
                if (data && data.length > 0) {
                    allData = [...allData, ...data];
                    if (data.length < limit) {
                        hasMore = false;
                    } else {
                        from += limit;
                    }
                } else {
                    hasMore = false;
                }
            }
            
            const records = allData.map(r => ({
                ...r,
                type: (r as any).type || 'exam',
                test_score: (r as any).test_score !== undefined && (r as any).test_score !== null ? (r as any).test_score : null,
                exam_score: (r as any).exam_score !== undefined && (r as any).exam_score !== null ? (r as any).exam_score : null,
                color: (r as any).color || '#000000',
                teacher_name: (r as any).teacher_name || ''
            })) as ExamRecord[];

            // Deduplicate duplicate records in database: keep the newest/most complete/submitted record
            const uniqueMap: Record<string, ExamRecord> = {};
            records.forEach(r => {
                const key = `${r.student_id}_${r.subject_id}_${r.term}_${r.academic_year_id}`;
                const existing = uniqueMap[key];
                if (existing) {
                    const keepNew = 
                        ((r.status === 'submitted' || r.status === 'approved') && (existing.status !== 'submitted' && existing.status !== 'approved')) ||
                        ((r.score !== null && r.score !== undefined && r.score !== '') && (existing.score === null || existing.score === undefined || existing.score === '')) ||
                        (new Date((r as any).created_at || 0) > new Date((existing as any).created_at || 0));
                    
                    if (keepNew) {
                        uniqueMap[key] = r;
                    }
                } else {
                    uniqueMap[key] = r;
                }
            });
            return Object.values(uniqueMap);
        } catch (err) {
            console.error('getExamRecordsByStudentIds error:', err);
            return [];
        }
    }

  async saveMarks(marksData: any[]) {
      try {
          // Validate data before sending
          const validMarks = marksData.filter(m => 
              m.student_id && 
              m.subject && 
              m.term && 
              typeof m.marks === 'number' && 
              m.marks >= 0 && 
              m.marks <= 100
          ).map(m => ({
              student_id: m.student_id,
              subject: m.subject,
              marks: m.marks,
              term: m.term,
              // Ensure deterministic IDs as requested: mark_${student_id}*${subject}*${term}
              id: `mark_${m.student_id}*${m.subject}*${m.term}`,
              school_id: m.school_id,
              created_at: new Date().toISOString()
          }));

          if (validMarks.length === 0) {
              throw new Error("No valid marks to save. Ensure marks are numbers between 0 and 100.");
          }

          console.log('[DB] saveMarks payload:', JSON.stringify(validMarks, null, 2));

          const { data, error } = await supabase
              .from('marks')
              .upsert(validMarks, { onConflict: 'student_id,subject,term' })
              .select();

          if (error) {
              console.warn('[DB] saveMarks Supabase error (expected if schema mismatch):', error.message);
              return null;
          }
          
          console.log('[DB] saveMarks success:', data?.length, 'records saved');
          return data;
      } catch (err) {
          console.warn('saveMarks caught error (suppressed):', err);
          return null;
      }
  }

  async saveExamRecords(records: ExamRecord[]) {
      try {
          // Fetch existing records for these students and subjects to prevent duplicate insertion
          const studentIds = Array.from(new Set(records.map(r => r.student_id)));
          const subjectIds = Array.from(new Set(records.map(r => r.subject_id)));
          const terms = Array.from(new Set(records.map(r => r.term)));
          const academicYearIds = Array.from(new Set(records.map(r => r.academic_year_id).filter(id => !!id)));
          
          let existingRecords: any[] = [];
          if (studentIds.length > 0 && subjectIds.length > 0) {
              let query = supabase
                  .from('exam_records')
                  .select('id, student_id, subject_id, term, academic_year_id, status, score, created_at')
                  .in('student_id', studentIds)
                  .in('subject_id', subjectIds)
                  .in('term', terms);
              
              if (academicYearIds.length > 0) {
                  query = query.in('academic_year_id', academicYearIds as string[]);
              }
              const { data } = await query;
              existingRecords = data || [];
          }
          
          const existingMap: Record<string, any> = {};
          existingRecords.forEach(er => {
              const key = `${er.student_id}_${er.subject_id}_${er.term}_${er.academic_year_id}`;
              const existing = existingMap[key];
              if (existing) {
                  const keepNew = 
                      ((er.status === 'submitted' || er.status === 'approved') && (existing.status !== 'submitted' && existing.status !== 'approved')) ||
                      ((er.score !== null && er.score !== undefined && er.score !== '') && (existing.score === null || existing.score === undefined || existing.score === '')) ||
                      (new Date((er as any).created_at || 0) > new Date((existing as any).created_at || 0));
                  if (keepNew) {
                      existingMap[key] = er;
                  }
              } else {
                  existingMap[key] = er;
              }
          });

          const recordsToUpsert = records.map(r => {
              const key = `${r.student_id}_${r.subject_id}_${r.term}_${r.academic_year_id}`;
              const existing = existingMap[key];
              const resolvedId = r.id || existing?.id || `er_${r.student_id}_${r.subject_id}_${r.term}_${r.academic_year_id}`;
              
              const record: any = {
                  id: resolvedId,
                  student_id: r.student_id,
                  student_name: r.student_name,
                  subject_id: r.subject_id,
                  subject_name: r.subject_name,
                  term: r.term,
                  school_id: r.school_id,
                  score: r.score,
                  max_score: r.max_score,
                  grade: r.grade,
                  remarks: r.remarks,
                  status: r.status,
                  position: r.position,
                  academic_year_id: r.academic_year_id,
                  teacher_id: r.teacher_id,
                  created_at: new Date().toISOString()
              };

              // Only add these if they are present in the record
              // We'll also handle the potential schema mismatch error later
              if (r.test_score !== undefined) record.test_score = r.test_score;
              if (r.exam_score !== undefined) record.exam_score = r.exam_score;
              
              return record;
          });

          const { error } = await supabase
              .from('exam_records')
              .upsert(recordsToUpsert);
          
          if (error) {
              // Handle "column does not exist" error by retrying without score breakdown
              if (error.message?.includes('column') && (error.message?.includes('exam_score') || error.message?.includes('test_score'))) {
                  console.warn('Schema mismatch detected (missing score columns). Retrying without breakdown...');
                  const safeRecords = recordsToUpsert.map(r => {
                      const { test_score, exam_score, ...rest } = r;
                      return rest;
                  });
                  const { error: retryError } = await supabase
                      .from('exam_records')
                      .upsert(safeRecords);
                  if (retryError) throw retryError;
              } else {
                  throw error;
              }
          }
      } catch (err) {
          console.error('saveExamRecords error:', err);
          throw err;
      }
  }

  async unlockMarks(schoolId: string, className: string, subjectId: string, term: string) {
      try {
          const students = await this.getStudents(schoolId, className);
          const studentIds = students.map(s => s.id);
          if (studentIds.length === 0) return;

          const { error } = await supabase
              .from('exam_records')
              .update({ status: 'draft' })
              .eq('school_id', schoolId)
              .eq('subject_id', subjectId)
              .eq('term', term)
              .in('student_id', studentIds);
          
          if (error) throw error;
      } catch (err) {
          console.error('unlockMarks error:', err);
          throw err;
      }
  }
  
  async getVerificationData(schoolId: string, classId: string, term: string) { return []; }
  async approveMarks(courseId: string, studentId?: string) {}
  
  async getPendingApprovalsCount(schoolId: string) { 
      try {
          const { count, error } = await supabase
              .from('marks_approvals')
              .select('*', { count: 'exact', head: true })
              .eq('school_id', schoolId)
              .eq('status', 'submitted');
          
          if (error) throw error;
          return count || 0;
      } catch (err) {
          console.error('getPendingApprovalsCount error:', err);
          return 0;
      }
  }

  async checkResultById(id: string) {
      try {
          const response = await fetch(`/api/check-result?id=${encodeURIComponent(id)}`);
          if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              throw new Error(errData.error || 'Result not found or API error.');
          }
          const result = await response.json();
          return result;
      } catch (err) {
          console.error('checkResultById error:', err);
          throw err;
      }
  }

  async checkResult(sdms: string, index: string) {
      try {
          const response = await fetch(`/api/check-result?sdms=${encodeURIComponent(sdms)}&index=${encodeURIComponent(index)}`);
          if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              throw new Error(errData.error || 'Result not found or API error.');
          }
          const result = await response.json();
          
          return result;
      } catch (err) {
          console.error('checkResult error:', err);
          throw err;
      }
  }

  async addMark(data: any) { 
      try {
          const { error } = await supabase
              .from('exam_records')
              .insert([data]);
          if (error) throw error;
      } catch (err) {
          console.error('addMark error:', err);
          throw err;
      }
  }

  // --- SITE AVAILABILITY ---
  async getSiteAvailability(): Promise<SiteAvailability> {
      const cacheKey = 'site_availability';
      const cached = this.getCache<SiteAvailability>(cacheKey);
      if (cached) return cached;

      try {
          const { data, error } = await supabase
              .from('site_availability')
              .select('*')
              .eq('id', 'default')
              .maybeSingle();
              
          if (!error && data) {
              const config = data as SiteAvailability;
              // Auto-lift check
              if (config.isMaintenanceMode && config.autoLift && config.endTime) {
                  const now = new Date();
                  const end = new Date(config.endTime);
                  if (now > end) {
                      const updated = {
                          ...config,
                          isMaintenanceMode: false,
                          lastUpdated: now.toISOString(),
                          updatedBy: 'System (Auto-lift)'
                      };
                      await supabase.from('site_availability').upsert({ ...updated, id: 'default' });
                      const finalConfig = { ...updated, whitelist: updated.whitelist || [] };
                      this.setCache(cacheKey, finalConfig, 5000);
                      return finalConfig;
                  }
              }
              const finalConfig = { ...config, whitelist: config.whitelist || [] };
              this.setCache(cacheKey, finalConfig, 5000);
              return finalConfig;
          }
          
          const defaultVal: SiteAvailability = {
              isMaintenanceMode: false,
              maintenanceType: 'maintenance',
              pageTitle: 'System Maintenance',
              visitorMessage: 'System is currently undergoing maintenance.',
              statusPageUrl: '',
              startTime: '',
              endTime: '',
              autoLift: false,
              whitelist: [],
              notifications: { email: false, slack: false },
              lastUpdated: new Date().toISOString(),
              updatedBy: 'System'
          };
          this.setCache(cacheKey, defaultVal, 5000);
          return defaultVal;
      } catch (err) {
          console.error('getSiteAvailability error:', err);
          const fallbackVal: SiteAvailability = {
              isMaintenanceMode: false,
              maintenanceType: 'maintenance',
              pageTitle: 'System Maintenance',
              visitorMessage: 'System is currently undergoing maintenance.',
              statusPageUrl: '',
              startTime: '',
              endTime: '',
              autoLift: false,
              whitelist: [],
              notifications: { email: false, slack: false },
              lastUpdated: new Date().toISOString(),
              updatedBy: 'System'
          };
          this.setCache(cacheKey, fallbackVal, 5000);
          return fallbackVal;
      }
  }

  async updateSiteAvailability(data: Partial<SiteAvailability>): Promise<SiteAvailability> {
      const current = await this.getSiteAvailability();
      const updated = { ...current, ...data, lastUpdated: new Date().toISOString() };
      
      try {
          const { error } = await supabase
              .from('site_availability')
              .upsert({ ...updated, id: 'default' });
          if (error) throw error;
      } catch (err) {
          console.error('updateSiteAvailability error:', err);
          throw err;
      }
      
      this.setCache('site_availability', updated, 5000);
      this.notifyAvailabilityListeners(updated);
      return updated;
  }

  subscribeToAvailability(callback: (config: SiteAvailability) => void) {
    // 1. Internal listeners (for immediate updates within the same session)
    this.availabilityListeners.push(callback);
    
    // 2. Supabase real-time listener
    const channel = supabase
        .channel('site_availability_changes')
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'site_availability', filter: "id=eq.default" },
            (payload) => {
                if (payload.new) {
                    const newConfig = payload.new as SiteAvailability;
                    this.setCache('site_availability', newConfig, 5000);
                    callback(newConfig);
                }
            }
        )
        .subscribe();
        
    return () => {
        supabase.removeChannel(channel);
        this.availabilityListeners = this.availabilityListeners.filter(l => l !== callback);
    };
  }

  private notifyAvailabilityListeners(config: SiteAvailability) {
    this.availabilityListeners.forEach(l => l(config));
  }
}

export const db = new DBService();
